import { useEffect, useState } from 'react';
import ScheduleToolsRow from '@/components/admin/schedule/ScheduleToolsRow';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, RefreshCw, Loader2, Users, Trophy, AlertTriangle, CheckCircle2, Circle, Settings, Pencil, Lock, LockOpen, UserMinus, ClipboardList, XCircle, ExternalLink, Globe, EyeOff, Eye, Gauge, Scale, Timer, ArrowLeftRight, CalendarClock, History, RotateCcw, Trash2, ChevronDown, Star, Info } from 'lucide-react';
import { toast } from 'sonner';
import { PublicResultsQR } from '@/components/admin/PublicResultsQR';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import EditGameModal from "@/components/admin/schedule/EditGameModal";
import ScheduleGrid from "@/components/admin/schedule/ScheduleGrid";
import ScheduleCheckPanel from "@/components/admin/schedule/ScheduleCheckPanel";
import BracketsPanel from "@/components/admin/schedule/BracketsPanel";
import PoolsPanel from "@/components/admin/schedule/PoolsPanel";
import BulkEditGrid from "@/components/admin/schedule/BulkEditGrid";

import WithdrawTeamDialog from "@/components/admin/schedule/WithdrawTeamDialog";
import SwapTeamsDialog, { type SwapTeamOption } from "@/components/admin/schedule/SwapTeamsDialog";
import { formatTimeRange, formatCalendarDate, formatTime } from "@/lib/dates";
import { getResolvedAvailability, computeCapacity } from "@/lib/eventAvailability";
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ResolvedAvailability } from '@/lib/eventAvailability';
import { SchedulingPageShell } from '@/components/admin/schedule/SchedulingPageShell';
import ScheduleHistoryPanel from '@/components/admin/schedule/ScheduleHistoryPanel';
import { ScheduleTabsEditor } from '@/components/admin/schedule/ScheduleTabsEditor';

/**
 * A wave control: "all X" until the director narrows it. Empty selection
 * always means EVERY option, never none — the same rule the placer uses for
 * a division's court allowlist.
 */
function WavePicker({
  options, selected, onChange, allLabel, noun, disabled, width = 'w-[150px]',
}: {
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
  allLabel: string;
  noun: string;
  disabled?: boolean;
  width?: string;
}) {
  const [open, setOpen] = useState(false);
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  const label = selected.length === 0
    ? allLabel
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? `1 ${noun}`)
      : `${selected.length} ${noun}s`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          role="combobox"
          aria-label={allLabel}
          disabled={disabled || options.length === 0}
          className={`h-9 justify-between font-normal ${width}`}
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2 bg-popover z-50">
        <div className="flex items-center justify-between px-1 pb-2">
          <span className="text-xs text-muted-foreground">{allLabel}</span>
          {selected.length > 0 && (
            <button type="button" className="text-xs underline" onClick={() => onChange([])}>
              Clear
            </button>
          )}
        </div>
        <div className="max-h-56 overflow-y-auto space-y-1">
          {options.map((o) => (
            <label
              key={o.value}
              className="flex items-center gap-2 rounded-md p-1.5 text-sm cursor-pointer hover:bg-accent"
            >
              <Checkbox checked={selected.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
              <span className="flex-1">{o.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}


/** "Sunday" style label for a scoped placement run. */
const formatScopeDate = (value: string) => formatCalendarDate(value, 'EEEE MMM d', value);

/** Human names for the placement order recorded in assignment_summary.order. */
const PLACEMENT_ORDER_LABELS: Record<string, string> = {
  most_restrictive: 'Most restrictive first',
  as_generated: 'As generated',
  by_pool: 'By pool',
  by_round: 'By round',
};



/**
 * Invoke a schedule edge function and always surface the real error message
 * from the response body instead of the generic "non-2xx status code" string.
 */
async function invokeSchedule(body: Record<string, unknown>): Promise<
  { ok: boolean; data?: any; message?: string }
> {
  const { data: res, error: fnError } = await invokeEdgeFunction(
    'rle-operator-schedule-draft',
    { body }
  );

  if (fnError) {
    let message = fnError.message || 'Request failed';
    // supabase-js attaches the raw Response on FunctionsHttpError.context for non-2xx
    try {
      const ctx = (fnError as any).context;
      if (ctx && typeof ctx.json === 'function') {
        const parsed = await ctx.json();
        message =
          parsed?.error?.message ||
          (typeof parsed?.error === 'string' ? parsed.error : null) ||
          parsed?.message ||
          message;
      } else if (ctx && typeof ctx.text === 'function') {
        const txt = await ctx.text();
        if (txt) message = txt;
      }
    } catch {
      /* keep default message */
    }
    return { ok: false, message };
  }

  if (!res?.ok) {
    return { ok: false, message: res?.error?.message || 'Request failed' };
  }
  return { ok: true, data: res.data };
}

export default function OperatorEventScheduleDraftPage() {
  const { eventId, section } = useParams<{ eventId: string; section?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  // The board's own unplaced total, reported up by ScheduleGrid (the same array
  // the "Unplaced games" tray counts) so the publish confirmation can never
  // disagree with the board. Null until the grid has reported.
  const [boardUnplaced, setBoardUnplaced] = useState<number | null>(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [placementConfirmation, setPlacementConfirmation] = useState<null | {
    permit: string;
    placed: number;
    unplaced: number;
    scoped: number;
    handEdited: number;
    locked: number;
    eventTotal: number;
    narrowed: boolean;
    scopeLabel: string;
    division: string;
    filters: string;
    /** Which wave this is: the configured one, top teams, or requests first. */
    preset: 'all' | 'top' | 'requests';
    /** "Into Court A, Court B — Fri 14 Nov 6:00pm–8:00pm", or null when wide open. */
    intoLine: string | null;
    /** Slots the chosen courts and window actually offer, null when unknown. */
    waveSlots: number | null;
    scope: {
      date: string | null;
      division: string | null;
      game_type: string | null;
      bracket_round: number | null;
      start_date: string | null;
      start_time: string | null;
      pools: string[];
      court_ids: string[];
      end_time: string | null;
      registration_ids: string[];
    };
    order: string;
    ignoreRequests: boolean;
    /** Per-run safety: reserve every game already on the board. */
    preserveAlreadyPlaced: boolean;
  }>(null);
  const [assigning, setAssigning] = useState(false);
  const [applyingConstraints, setApplyingConstraints] = useState(false);

  // Late registrations / withdrawals against the current draft. The counts come
  // from a dry run of sync_teams, so the button can never promise something the
  // action would not do.
  const [teamDelta, setTeamDelta] = useState<{
    new_teams: Array<{ registration_id: string; name: string; division: string | null }>;
    withdrawn_teams: Array<{ registration_id: string; name: string; division: string | null }>;
  } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState<any>(null);

  // Short pools: how many games each team actually gets versus what the event
  // promised, plus the remedies costed from real rows.
  const [rosterImpact, setRosterImpact] = useState<any>(null);
  const [applyingRemedy, setApplyingRemedy] = useState<string | null>(null);
  const [coachMessages, setCoachMessages] = useState<Array<{ team: string; message: string }> | null>(null);



  // Manual override detail (courts, sessions, per-game/per-team ids)
  const [detail, setDetail] = useState<any>(null);
  const [editGame, setEditGame] = useState<any>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [withdrawTeam, setWithdrawTeam] = useState<any>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [swapDivision, setSwapDivision] = useState<{ division: string; teams: SwapTeamOption[] } | null>(null);


  // Coach scheduling-request surfacing
  const [pendingRequests, setPendingRequests] = useState(0);
  const [outcomes, setOutcomes] = useState<any[]>([]);

  // Schedule quality summary (measured from existing draft data, not recomputed)
  const [quality, setQuality] = useState<any>(null);

  // Result of the last "Place games" run (assignment_summary from assign_slots)
  const [placement, setPlacement] = useState<any>(null);
  // Last failure from a "Place games" run, surfaced in the Placement panel.
  const [placementError, setPlacementError] = useState<string | null>(null);

  // What-if only: place without honouring any scheduling request. Deliberately
  // NOT persisted anywhere — it resets to off on every load and after each run.
  const [ignoreRequests, setIgnoreRequests] = useState(false);
  // The change history sheet, and whether the board has taken the whole pane.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [gridFullscreen, setGridFullscreen] = useState(false);

  // Which view of the draft is showing. It lives in the URL so every screen
  // can be bookmarked or sent to someone mid-event.
  //   /scheduling/grid · /scheduling/bulk-edit · /scheduling/snapshots
  // The legacy /schedule-draft route (no section) keeps the old combined page.
  const view: 'pools' | 'grid' | 'bulk' | 'snapshots' =
    section === 'grid' ? 'grid'
      : section === 'bulk-edit' ? 'bulk'
      : section === 'snapshots' ? 'snapshots'
      : 'pools';
  const setView = (next: 'pools' | 'grid' | 'bulk' | 'snapshots') => {
    const seg = next === 'bulk' ? 'bulk-edit' : next;
    navigate(`/admin-dashboard/events/${eventId}/scheduling/${seg}`);
  };
  const [gridRefreshToken, setGridRefreshToken] = useState(0);


  // Which games the next run is allowed to touch. 'all' everywhere = today's behaviour.
  const [scopeDate, setScopeDate] = useState<string>('all');
  const [scopeDivision, setScopeDivision] = useState<string>('all');
  const [scopeGameType, setScopeGameType] = useState<string>('all');
  // Bracket round scope — 'all', or a specific bracket_round. A round is
  // implicitly a bracket scope: pool play has no rounds.
  const [scopeRound, setScopeRound] = useState<string>('all');
  // Start-from cutoff: a date, and optionally a time on that date.
  const [scopeStartDate, setScopeStartDate] = useState<string>('');
  const [scopeStartTime, setScopeStartTime] = useState<string>('');
  // ─── The rest of a wave: which pools, which courts, and the closing time.
  // Empty always means "all of them", never none.
  const [scopePools, setScopePools] = useState<string[]>([]);
  const [scopeCourts, setScopeCourts] = useState<string[]>([]);
  const [scopeEndTime, setScopeEndTime] = useState<string>('');

  // Resolved court hours, kept so the dialog can say how many games fit.
  const [availability, setAvailability] = useState<ResolvedAvailability | null>(null);
  const [slotMinutes, setSlotMinutes] = useState<number | null>(null);



  // Order candidates are tried in. Rules are unchanged by this — only order.
  const [placementOrderChoice, setPlacementOrderChoice] = useState<string>('most_restrictive');



  const [venueNames, setVenueNames] = useState<Record<string, string>>({});

  // Publish state
  const [publishStatus, setPublishStatus] = useState<any>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  // Item 8: changes since the last publish, and whether to tell those teams.
  // On by default every time the dialog opens — it never remembers "off".
  const [pubChanges, setPubChanges] = useState<any>(null);
  const [pubChangesLoading, setPubChangesLoading] = useState(false);
  const [notifyChanges, setNotifyChanges] = useState(true);
  useEffect(() => {
    if (!publishOpen) return;
    setNotifyChanges(true);
    setPubChanges(null);
    if (!publishStatus?.published || !eventId) return;
    let alive = true;
    setPubChangesLoading(true);
    invokeEdgeFunction('rle-operator-schedule-manual', { body: { action: 'publish_changes', event_id: eventId } })
      .then(({ data: res }: any) => { if (alive && res?.ok) setPubChanges(res.data); })
      .finally(() => { if (alive) setPubChangesLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishOpen]);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  // The check before you publish — read-only diagnosis, re-runnable on demand.
  const [check, setCheck] = useState<any>(null);
  const [checking, setChecking] = useState(false);
  const [checkOpen, setCheckOpen] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [gridFocus, setGridFocus] = useState<{ date: string | null; gameId: string; nonce: number } | null>(null);
  useEffect(() => {
    if (section !== 'grid') return;
    const gameId = searchParams.get('game');
    if (!gameId) return;
    setGridFocus({ date: searchParams.get('date'), gameId, nonce: Date.now() });
  }, [section, searchParams]);

  // ─── History (placement snapshots) ──────────────────────────────────────
  // Snapshots record where games sit, never who plays whom.
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveLabel, setSaveLabel] = useState('');
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<any>(null);
  const [restorePreview, setRestorePreview] = useState<any>(null);
  const [previewing, setPreviewing] = useState(false);
  const [restoring, setRestoring] = useState(false);



  // Public link for the QR code — the slug is the public route key.
  const [eventSlug, setEventSlug] = useState<string | null>(null);
  useEffect(() => {
    if (!eventId) return;
    let alive = true;
    (async () => {
      const { data: row } = await (supabase as any)
        .from('events').select('slug').eq('id', eventId).maybeSingle();
      if (alive) setEventSlug(row?.slug ?? null);
    })();
    return () => { alive = false; };
  }, [eventId]);

  const fetchPublishStatus = async () => {
    if (!eventId) return;
    try {
      const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'publish_status', event_id: eventId },
      });
      if (res?.ok) setPublishStatus(res.data);
    } catch { /* non-fatal */ }
  };

  const fetchCheck = async () => {
    if (!eventId) return null;
    setChecking(true);
    try {
      const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'publish_check', event_id: eventId },
      });
      if (res?.ok) { setCheck(res.data); return res.data; }
      return null;
    } catch { return null; } finally { setChecking(false); }
  };

  /** Jump into the grid on that day with that game ringed. */
  const focusGame = (g: any) => {
    setGridFocus({ date: g?.date ?? null, gameId: g.game_id, nonce: Date.now() });
    document.getElementById('schedule-grid-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const afterDialogClose = (refresh: () => void) => {
    requestAnimationFrame(() => requestAnimationFrame(refresh));
  };

  const handlePublish = async (overrideErrors = false) => {
    setPublishing(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'publish', event_id: eventId, force: true, override_errors: overrideErrors, notify_changes: !!(pubChanges?.change_count > 0 && notifyChanges) },
      });
      if (error || !res?.ok) {
        // The check runs again server-side at the moment of publishing.
        if (res?.error?.code === 'PUBLISH_CHECK_FAILED') {
          setCheck(res.data);
          setPublishOpen(false);
          setCheckOpen(true);
          toast.error(res.error.message);
          return;
        }
        console.error('[publish] failed', error || res?.error);
        toast.error(res?.error?.code === 'PENDING_REQUESTS' ? res.error.message : 'Publishing failed. Nothing has changed.');
        return;
      }
      if (res.data?.publish_check) setCheck(res.data.publish_check);
      toast.success('Schedule published — it is now live on the public event page.');
      const n = res.data?.notified;
      if (n) {
        const parts = [`${n.sent} ${n.sent === 1 ? 'team' : 'teams'} emailed about their changes`];
        if (n.unreachable) parts.push(`${n.unreachable} could not be reached (no coach email)`);
        if (n.failed) parts.push(`${n.failed} failed — see History`);
        (n.failed ? toast.warning : toast.success)(parts.join(' · '));
      }
      setPublishStatus(res.data);
      setPublishOpen(false);
      setOverrideOpen(false);
      afterDialogClose(() => {
        fetchDraft();
        fetchDetail();
      });
    } finally { setPublishing(false); }
  };

  const handleUnpublish = async () => {
    setUnpublishing(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'unpublish', event_id: eventId },
      });
      if (error || !res?.ok) {
        console.error('[unpublish] failed', error || res?.error);
        toast.error('Unpublishing failed. Nothing has changed.');
        return;
      }
      toast.success('Schedule unpublished — it is hidden from the public page and editable again.');
      setPublishStatus(res.data);
      setUnpublishOpen(false);
      afterDialogClose(() => {
        fetchDraft();
        fetchDetail();
      });
    } finally { setUnpublishing(false); }
  };


  const fetchDetail = async () => {
    if (!eventId) return;
    try {
      const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'detail', event_id: eventId },
      });
      if (res?.ok) {
        setDetail(res.data);
        fetchOutcomes(res.data?.schedule_version_id);
      }
    } catch { /* non-fatal; manual controls just won't prefill */ }
    fetchQuality();
  };

  // Quality summary: balance, rest violations, % approved constraints honored.
  const fetchQuality = async () => {
    if (!eventId) return;
    try {
      const { data: res } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'quality_summary', event_id: eventId },
      });
      if (res?.ok) setQuality(res.data?.has_draft ? res.data : null);
      else setQuality(null);
    } catch { setQuality(null); }
  };




  const fetchSchedulingRequests = async () => {
    if (!eventId) return;
    try {
      const { count } = await (supabase as any)
        .from('schedule_constraints')
        .select('id', { count: 'exact', head: true })
        .eq('event_id', eventId)
        .is('deleted_at', null)
        .eq('status', 'pending_review');
      setPendingRequests(count ?? 0);
    } catch { setPendingRequests(0); }
  };

  // Outcomes for the current draft version: which approved constraints were honored/violated.
  const fetchOutcomes = async (versionId?: string | null) => {
    if (!eventId || !versionId) { setOutcomes([]); return; }
    try {
      const { data: rows } = await (supabase as any)
        .from('schedule_constraint_outcomes')
        .select('*')
        .eq('event_id', eventId)
        .eq('schedule_version_id', versionId);
      setOutcomes(rows ?? []);
    } catch { setOutcomes([]); }
  };

  const toggleLock = async (gameId: string, locked: boolean) => {
    const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'set_lock', event_id: eventId, game_id: gameId, locked },
    });
    if (error || !res?.ok) {
      toast.error(error?.message || res?.error?.message || 'Could not update lock.');
      return;
    }
    toast.success(locked ? 'Game locked.' : 'Game unlocked.');
    fetchDetail();
  };

  const flipHomeAway = async (gameId: string) => {
    const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: { action: 'flip_home_away', event_id: eventId, game_id: gameId },
    });
    if (error || !res?.ok) {
      toast.error(error?.message || res?.error?.message || 'Could not flip home/away.');
      return;
    }
    toast.success('Home / away flipped.');
    fetchDetail();
  };

  const openEditGame = (g: any) => {
    setEditGame(g);
    setEditOpen(true);
  };

  const afterManualChange = () => { fetchDraft(); fetchDetail(); };


  // Pre-flight prerequisites for generation
  const [prereqLoading, setPrereqLoading] = useState(true);
  const [courtCount, setCourtCount] = useState(0);
  const [sessionCount, setSessionCount] = useState(0);
  const [teamCount, setTeamCount] = useState(0);

  const fetchDraft = async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: fnError } = await invokeEdgeFunction(
        'rle-operator-schedule-draft',
        { body: { action: 'get', event_id: eventId } }
      );
      if (fnError || !res?.ok) setError(res?.error?.message || fnError?.message || 'Could not load draft.');
      else setData(res.data);
    } catch (e: any) { setError(e?.message ?? 'Unexpected error'); }
    finally { setLoading(false); }
  };

  // Pre-flight warnings shown BEFORE "Place games" is pressed. Never blocking.
  const [preflight, setPreflight] = useState<{
    unpooled: Array<{ name: string; division: string | null }>;
    gamesToPlace: number;
    slots: number | null;
    placedGames: number;
    adjustedGames: number;
  } | null>(null);


  const fetchPreflight = async () => {
    if (!eventId) return;
    try {
      const [res, fmtRes, resolved] = await Promise.all([
        invokeSchedule({ action: 'preflight', event_id: eventId }),
        (supabase as any)
          .from('event_format_config')
          .select('game_duration_minutes, buffer_minutes_between_games')
          .eq('event_id', eventId)
          .maybeSingle(),
        getResolvedAvailability(eventId).catch(() => null),
      ]);
      if (!res.ok) { setPreflight(null); return; }

      const gl = Number((fmtRes as any)?.data?.game_duration_minutes ?? 0);
      const ta = Number((fmtRes as any)?.data?.buffer_minutes_between_games ?? 0);
      const slotMinutes = gl > 0 ? gl + (ta || 0) : null;
      // Same helper the Availability tab uses, so the two can never disagree.
      const capacity = resolved ? computeCapacity(resolved, slotMinutes) : null;
      const slots = capacity ? capacity.reduce((a, d) => a + d.slots, 0) : null;
      // Kept so a wave can be measured against the courts and window chosen.
      setAvailability(resolved ?? null);
      setSlotMinutes(slotMinutes);

      setPreflight({
        unpooled: (res.data?.unpooled_teams ?? []).map((t: any) => ({
          name: t.name, division: t.division,
        })),
        gamesToPlace: res.data?.games_to_place ?? 0,
        slots,
        placedGames: res.data?.placed_games ?? 0,
        adjustedGames: res.data?.manually_adjusted_games ?? 0,
      });
    } catch { setPreflight(null); }
  };

  // Read-only: pool sizes versus the promised games-per-team.
  const fetchRosterImpact = async () => {
    if (!eventId) return;
    try {
      const res = await invokeSchedule({ action: 'roster_impact', event_id: eventId });
      setRosterImpact(res.ok ? res.data : null);
    } catch { setRosterImpact(null); }
  };

  const handleApplyRemedy = async (remedy: any) => {
    if (applyingRemedy) return;
    setApplyingRemedy(remedy.id);
    setCoachMessages(null);
    try {
      const result = await invokeSchedule({
        action: 'apply_remedy', event_id: eventId, op: remedy.op, params: remedy.params,
      });
      if (!result.ok) { toast.error(result.message || 'Could not apply that fix'); return; }
      setData(result.data);
      setRosterImpact(result.data?.roster_impact ?? null);
      const a = result.data?.remedy_applied;
      if (a?.op === 'accept') {
        setCoachMessages(a.coach_messages ?? []);
        toast.success('Drafted a note for each affected coach.');
      } else {
        toast.success(
          [
            a?.games_created ? `${a.games_created} game${a.games_created === 1 ? '' : 's'} added` : null,
            a?.games_deleted ? `${a.games_deleted} removed` : null,
          ].filter(Boolean).join(' · ') || 'Applied.',
        );
      }
      setGridRefreshToken((token) => token + 1);
      fetchDetail();
      fetchPreflight();
    } catch (e: any) {
      toast.error(e?.message || 'Could not apply that fix');
    } finally { setApplyingRemedy(null); }
  };


  // Dry run of sync_teams — the exact comparison the real run performs.
  const fetchTeamDelta = async () => {
    if (!eventId) return;
    try {
      const res = await invokeSchedule({ action: 'sync_teams', event_id: eventId, dry_run: true });
      if (!res.ok) { setTeamDelta(null); return; }
      setTeamDelta({
        new_teams: res.data?.new_teams ?? [],
        withdrawn_teams: res.data?.withdrawn_teams ?? [],
      });
    } catch { setTeamDelta(null); }
  };



  const fetchPrerequisites = async () => {
    if (!eventId) return;
    setPrereqLoading(true);
    try {
      const [setupRes, teamsRes] = await Promise.all([
        invokeEdgeFunction('rle-operator-event-schedule-setup', {
          body: { event_id: eventId },
        }),
        (supabase as any)
          .from('event_registrations_v2')
          .select('recruitlook_team_id', { count: 'exact', head: true })
          .eq('event_id', eventId)
          .in('registration_status', ['registered', 'paid', 'completed'])
          .not('recruitlook_team_id', 'is', null),
      ]);

      const setupData = (setupRes as any)?.data?.data;
      const courts = (setupData?.courts ?? []).filter((c: any) => c.is_active !== false);
      const sessions = (setupData?.sessions ?? []).filter((s: any) => s.is_active !== false);
      setCourtCount(courts.length);
      setSessionCount(sessions.length);
      setTeamCount((teamsRes as any)?.count ?? 0);
    } catch (e) {
      // Never let a failed pre-flight read block generation — the edge function
      // performs its own authoritative validation and returns a real error.
      console.warn('[schedule-draft] prerequisite check failed', e);
    } finally {
      setPrereqLoading(false);
    }
  };

  useEffect(() => { fetchDraft(); fetchPrerequisites(); fetchDetail(); fetchSchedulingRequests(); fetchPublishStatus(); fetchPreflight(); fetchTeamDelta(); fetchRosterImpact(); fetchCheck(); /* eslint-disable-next-line */ }, [eventId]);


  const requirements = [
    { label: 'At least one active court', met: courtCount > 0, section: 'courts' },
    { label: 'At least one active time slot / session', met: sessionCount > 0, section: 'sessions' },
    { label: 'At least one registered team', met: teamCount > 0, section: 'teams' },
  ];
  const metCount = requirements.filter((r) => r.met).length;
  const canGenerate = !prereqLoading && metCount === requirements.length;

  const goToSetup = (section?: string) =>
    navigate(`/admin-dashboard/events/${eventId}/schedule-setup${section ? `#${section}` : ''}`);

  const goToVenues = (tab: string) =>
    navigate(`/admin-dashboard/events/${eventId}/venues-courts?tab=${tab}`);

  // Total games in the current draft — works for both `generate` and `get` responses.
  // `get` returns games nested under pools_by_division; `generate` also adds total_games,
  // so derive from the nested structure to keep the two responses consistent.
  const draftGameCount = (data?.pools_by_division ?? []).reduce(
    (total: number, division: any) =>
      total + (division?.pools ?? []).reduce(
        (poolTotal: number, pool: any) => poolTotal + (pool?.games?.length ?? 0),
        0
      ),
    0
  );

  // ─── Placement scope options ───────────────────────────────────────────
  // pools_by_division only carries POOL games — bracket and consolation games
  // have no pool. Every scope count reads `scope_games`, which the edge
  // function builds from the very rows the placement run acts on.
  const poolOnlyGames: any[] = (data?.pools_by_division ?? []).flatMap((d: any) =>
    (d?.pools ?? []).flatMap((p: any) => (p?.games ?? []).map((g: any) => ({ ...g, division: d.division }))),
  );
  const allDraftGames: any[] = (data as any)?.scope_games?.length
    ? ((data as any).scope_games as any[])
    : poolOnlyGames;
  const scopeDateOptions: string[] = [...new Set(
    allDraftGames.map((g) => (g.scheduled_date ? String(g.scheduled_date).slice(0, 10) : null)).filter(Boolean) as string[],
  )].sort();
  const scopeDivisionOptions: string[] = [...new Set([
    ...(data?.pools_by_division ?? []).map((d: any) => d.division),
    ...allDraftGames.map((g) => g.division),
  ].filter(Boolean) as string[])];

  // Pools of the divisions in scope — the games themselves carry the pool, so
  // narrowing the division narrows this list without a second source of truth.
  const poolScopeOptions: string[] = [...new Set(
    allDraftGames
      .filter((g) => scopeDivision === 'all' || g.division === scopeDivision)
      .map((g) => (g.pool_name ? String(g.pool_name) : null))
      .filter(Boolean) as string[],
  )].sort();
  // Every court this event runs on. Empty selection = all of them.
  const courtScopeOptions: Array<{ id: string; name: string }> =
    ((data as any)?.event_courts as any[] | undefined)?.map((c) => ({ id: String(c.id), name: String(c.name) }))
    ?? (availability?.courts ?? []).map((c) => ({ id: c.court_id, name: c.court_name }));
  const courtNameById = new Map(courtScopeOptions.map((c) => [c.id, c.name]));


  // Rounds that actually exist in this version, labelled the way the bracket
  // labels them (Final / Semifinals / Quarterfinals / Round N).
  const bracketRounds: number[] = [...new Set(
    allDraftGames
      .filter((g) => ['bracket', 'consolation'].includes(g.game_type) && g.bracket_round != null)
      .map((g) => Number(g.bracket_round))
      .filter((n) => Number.isFinite(n)),
  )].sort((a, b) => a - b);
  const maxRound = bracketRounds.length ? bracketRounds[bracketRounds.length - 1] : 0;
  const roundLabel = (round: number) => {
    const fromEnd = maxRound - round;
    if (fromEnd === 0) return 'Final';
    if (fromEnd === 1) return 'Semifinals';
    if (fromEnd === 2) return 'Quarterfinals';
    return `Round ${round}`;
  };

  // Round + "pool only" is a contradiction, refused rather than run empty.
  const scopeConflict =
    scopeRound !== 'all' && scopeGameType === 'pool'
      ? 'Pool play has no rounds — pick bracket games (or all games) to place a round.'
      : null;

  const startFromLabel = scopeStartDate
    ? `from ${formatScopeDate(scopeStartDate)}${scopeStartTime ? ` ${formatTime(scopeStartTime)}` : ''}`
    : null;
  const gameTypeScopeLabel = (type: string) =>
    type === 'bracket' ? 'bracket & consolation' : type;
  const poolsLabel = scopePools.length ? scopePools.join(', ') : null;
  const scopeLabel = [
    scopeDate !== 'all' ? formatScopeDate(scopeDate) : null,
    scopeDivision !== 'all' ? scopeDivision : null,
    poolsLabel,
    scopeRound !== 'all' ? roundLabel(Number(scopeRound)) : (scopeGameType !== 'all' ? gameTypeScopeLabel(scopeGameType) : null),
  ].filter(Boolean).join(', ');
  const placeButtonLabel = scopeLabel || startFromLabel
    ? `Place ${scopeLabel || 'all'} games${startFromLabel ? ` ${startFromLabel}` : ''}`
    : 'Place games';

  // ─── What a "Place games" run would land on ───────────────────────────
  // The confirmation is about HIS board: how many games already carry a
  // court and a time inside the scope actually in force.
  const gameIsPlaced = (g: any) =>
    g?.is_placed === true || Boolean(
      (g?.court_id || g?.court_name) && g?.scheduled_date && g?.scheduled_start_time,
    );

  /**
   * The wave, as a filter. `preset` narrows it further to starred teams or to
   * teams carrying an approved scheduling request — the same set the server
   * receives as registration_ids, taken from the same scope_games list.
   */
  const filterWave = (preset: 'all' | 'top' | 'requests') => allDraftGames.filter((g) => {
    if (scopeDate !== 'all' && String(g.scheduled_date ?? '').slice(0, 10) !== scopeDate) return false;
    if (scopeDivision !== 'all' && g.division !== scopeDivision) return false;
    if (scopeGameType !== 'all') {
      if (scopeGameType === 'bracket') {
        if (!['bracket', 'consolation'].includes(g.game_type)) return false;
      } else if (g.game_type !== scopeGameType) {
        return false;
      }
    }
    if (scopeRound !== 'all' && Number(g.bracket_round) !== Number(scopeRound)) return false;
    if (scopePools.length && !scopePools.includes(String(g.pool_name ?? ''))) return false;
    if (preset === 'top' && !g.is_priority) return false;
    if (preset === 'requests' && !g.has_request) return false;
    if (scopeStartDate) {
      const d = String(g.scheduled_date ?? '').slice(0, 10);
      if (d && d < scopeStartDate) return false;
    }
    return true;
  });

  /** The registration ids a preset wave is about, for the server's filter. */
  const waveRegistrationIds = (games: any[]): string[] => [...new Set(
    games.flatMap((g) => [g.team_a_registration_id, g.team_b_registration_id])
      .filter(Boolean).map(String),
  )];

  const starredGameCount = allDraftGames.filter((g) => g.is_priority).length;
  const requestGameCount = allDraftGames.filter((g) => g.has_request).length;

  const gamesInScope: any[] = filterWave('all');
  const placedInScope = gamesInScope.filter(gameIsPlaced).length;
  const unplacedInScope = gamesInScope.length - placedInScope;
  // Hand edits, from the same in-scope set the counts above use.
  const handEditedInScope = gamesInScope.filter((g) => g?.manually_adjusted || g?.locked).length;
  const lockedInScope = gamesInScope.filter((g) => g?.locked).length;
  const scopeIsNarrowed =
    scopeDate !== 'all' || scopeDivision !== 'all' || scopeGameType !== 'all' ||
    scopeRound !== 'all' || Boolean(scopeStartDate) ||
    scopePools.length > 0 || scopeCourts.length > 0 || Boolean(scopeEndTime);
  const orderLabel = ({
    most_restrictive: 'most restrictive first',
    as_generated: 'as generated',
    by_pool: 'by pool',
    by_round: 'by round',
  } as Record<string, string>)[placementOrderChoice] ?? placementOrderChoice;
  const courtsLabel = scopeCourts.length
    ? scopeCourts.map((id) => courtNameById.get(id) ?? 'court').join(', ')
    : 'every court';
  const windowLabel = scopeEndTime
    ? `${formatTime(scopeStartTime || '00:00')}–${formatTime(scopeEndTime)}`
    : null;
  const filtersInForceLine = [
    scopeDate !== 'all' ? formatScopeDate(scopeDate) : 'all days',
    scopeDivision !== 'all' ? `${scopeDivision} only` : 'all divisions',
    poolsLabel ? `${poolsLabel} only` : null,
    scopeRound !== 'all'
      ? `${roundLabel(Number(scopeRound))} only`
      : (scopeGameType !== 'all' ? `${gameTypeScopeLabel(scopeGameType)} games only` : 'all games'),
    startFromLabel,
    orderLabel,
  ].filter(Boolean).join(', ');

  /**
   * How many games the chosen courts and window can actually hold — read from
   * the same resolved availability the placer uses, so the dialog can say
   * "24 games, 16 slots in that window" BEFORE he commits.
   */
  const waveSlotCount = (): number | null => {
    if (!availability || !slotMinutes || slotMinutes <= 0) return null;
    const toMin = (t: string) => {
      const [h, m] = String(t).split(':');
      return Number(h) * 60 + Number(m);
    };
    const from = scopeEndTime ? toMin(scopeStartTime || '00:00') : null;
    const until = scopeEndTime ? toMin(scopeEndTime) : null;
    const allowCourt = scopeCourts.length ? new Set(scopeCourts) : null;
    let slots = 0;
    for (const c of availability.courts) {
      if (allowCourt && !allowCourt.has(c.court_id)) continue;
      for (const [date, windows] of Object.entries(c.by_date)) {
        if (scopeDate !== 'all' && date !== scopeDate) continue;
        if (scopeStartDate && date < scopeStartDate) continue;
        for (const w of windows) {
          let start = toMin(w.start_time);
          let end = toMin(w.end_time);
          if (from != null) start = Math.max(start, from);
          if (until != null) end = Math.min(end, until);
          if (scopeStartDate && date === scopeStartDate && scopeStartTime) {
            start = Math.max(start, toMin(scopeStartTime));
          }
          if (end > start) slots += Math.floor((end - start) / slotMinutes);
        }
      }
    }
    return slots;
  };







  // Why "Place games" cannot run yet — never let the operator hit a flat 400.
  const placementBlocker: { message: string; cta: string; go: () => void } | null =
    prereqLoading
      ? null
      : courtCount === 0
        ? { message: 'No active courts yet — add courts before placing games.', cta: 'Venues & Courts → Courts', go: () => goToVenues('courts') }
        : sessionCount === 0
          ? { message: 'No availability windows yet — set the hours each court is open.', cta: 'Venues & Courts → Availability', go: () => goToVenues('availability') }
          : !data?.schedule_version || draftGameCount === 0
            ? { message: 'No draft generated yet — create pools and games first.', cta: 'Use Create pools & games above', go: () => setRegenOpen(true) }
            : null;

  // Warnings — surfaced before the button is pressed, never blocking it.
  //
  // A brand new event has no pools at all, so EVERY team is "unpooled". That is
  // not something going wrong; it is what a new event looks like. Say so plainly
  // and keep the warning (and the list of names) for the real case: an event
  // that HAS pools with a few teams sitting outside them.
  const noPoolsYet = (data?.pools_by_division ?? []).length === 0;
  const unpooledNotice =
    preflight && preflight.unpooled.length > 0
      ? noPoolsYet
        ? {
            tone: 'info' as const,
            text:
              `No pools yet. Generate pools to put your ${preflight.unpooled.length} team` +
              `${preflight.unpooled.length === 1 ? '' : 's'} into pools and create their games.`,
          }
        : {
            tone: 'warn' as const,
            text:
              `${preflight.unpooled.length} team${preflight.unpooled.length === 1 ? " isn't" : "s aren't"} in a pool and will be skipped: ` +
              preflight.unpooled
                .map((t) => (t.division ? `${t.name} (${t.division})` : t.name))
                .join(', ') +
              '. Use Update teams to add them.',
          }
      : null;
  const unpooledWarning = unpooledNotice?.text ?? null;

  const shortfall =
    preflight && preflight.slots != null && preflight.gamesToPlace > preflight.slots
      ? preflight.gamesToPlace - preflight.slots
      : 0;
  const capacityWarning =
    shortfall > 0 && preflight
      ? `${preflight.gamesToPlace} games, ${preflight.slots} slots available. ${shortfall} game${shortfall === 1 ? '' : 's'} will not fit. Add court hours or shorten the game.`
      : null;



  // Advisory only: the checklist may be stale or unreadable, so always let the
  // operator proceed. The edge function validates and reports the real reason.
  //
  // Starting over is destructive, so it is confirmed — but only when there is
  // actually something to lose. Nothing placed and nothing hand-adjusted means
  // nothing to protect, so the dialog is skipped.
  const startOverLoss = (preflight?.placedGames ?? 0) + (preflight?.adjustedGames ?? 0);
  const openRegen = () => {
    if (startOverLoss === 0) { void handleRegenerate(); return; }
    setRegenOpen(true);
  };

  // ─── Update teams ───────────────────────────────────────────────────────
  const newTeamCount = teamDelta?.new_teams.length ?? 0;
  const withdrawnTeamCount = teamDelta?.withdrawn_teams.length ?? 0;
  const teamChanges = newTeamCount + withdrawnTeamCount;
  const syncButtonLabel = teamChanges === 0
    ? 'Update teams'
    : `Update teams (${[
        newTeamCount ? `${newTeamCount} new` : null,
        withdrawnTeamCount ? `${withdrawnTeamCount} withdrawn` : null,
      ].filter(Boolean).join(', ')})`;

  const handleSyncTeams = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const result = await invokeSchedule({ action: 'sync_teams', event_id: eventId });
      if (!result.ok) {
        toast.error(result.message || 'Could not update teams');
        return;
      }
      const s = result.data?.sync_summary;
      setSyncSummary(s ?? null);
      setData(result.data);
      setGridRefreshToken((token) => token + 1);
      toast.success(
        [
          s?.teams_added?.length ? `${s.teams_added.length} team${s.teams_added.length === 1 ? '' : 's'} added` : null,
          s?.teams_removed?.length ? `${s.teams_removed.length} withdrawn` : null,
          s?.games_created ? `${s.games_created} game${s.games_created === 1 ? '' : 's'} created` : null,
          s?.games_deleted ? `${s.games_deleted} deleted` : null,
        ].filter(Boolean).join(' · ') || 'Teams already in sync.',
      );
      fetchDetail();
      fetchPreflight();
      fetchTeamDelta(); fetchRosterImpact();
    } catch (e: any) {
      toast.error(e?.message || 'Could not update teams');
    } finally { setSyncing(false); }
  };




  // ─── History: list, save, restore ───────────────────────────────────────
  const fetchSnapshots = async () => {
    if (!eventId) return;
    const result = await invokeSchedule({ action: 'list_snapshots', event_id: eventId });
    if (result.ok) setSnapshots(result.data?.snapshots ?? []);
  };

  const handleSaveSnapshot = async () => {
    if (savingSnapshot) return;
    setSavingSnapshot(true);
    try {
      const result = await invokeSchedule({
        action: 'create_snapshot', event_id: eventId, label: saveLabel.trim() || 'Saved arrangement',
      });
      if (!result.ok) { toast.error(result.message || 'Could not save this arrangement'); return; }
      toast.success('Arrangement saved. You can come back to it any time.');
      setSaveOpen(false);
      setSaveLabel('');
      fetchSnapshots();
    } finally { setSavingSnapshot(false); }
  };

  /** Open the restore dialog with a dry run — never restores anything itself. */
  const openRestore = async (snapshot: any) => {
    setRestoreTarget(snapshot);
    setRestorePreview(null);
    setPreviewing(true);
    try {
      const result = await invokeSchedule({
        action: 'restore_snapshot', event_id: eventId, snapshot_id: snapshot.id, dry_run: true,
      });
      if (!result.ok) { toast.error(result.message || 'Could not preview this restore'); setRestoreTarget(null); return; }
      setRestorePreview(result.data);
    } finally { setPreviewing(false); }
  };

  const handleRestore = async () => {
    if (!restoreTarget || restoring) return;
    setRestoring(true);
    try {
      const result = await invokeSchedule({
        action: 'restore_snapshot', event_id: eventId, snapshot_id: restoreTarget.id,
        // The dry run tells us whether coaches are already reading this schedule;
        // the dialog says so before this point is reached.
        confirm_published: restorePreview?.published === true,
      });
      if (!result.ok) { toast.error(result.message || 'Restore failed'); return; }

      const s = result.data?.restore_summary;
      setData(result.data);
      setGridRefreshToken((token) => token + 1);
      setRestoreTarget(null);
      setRestorePreview(null);
      toast.success(
        `Restored "${s?.snapshot?.label ?? 'snapshot'}" — ${s?.games_moving ?? 0} game${(s?.games_moving ?? 0) === 1 ? '' : 's'} moved back.`,
      );
      afterDialogClose(() => {
        fetchDetail();
        fetchPreflight();
        fetchSnapshots();
        fetchPublishStatus();   // the public page is now behind
        fetchTeamDelta(); fetchRosterImpact();
      });
    } catch (e: any) {
      toast.error(e?.message || 'Restore failed');
    } finally { setRestoring(false); }
  };

  const handleDeleteSnapshot = async (snapshot: any) => {
    const result = await invokeSchedule({
      action: 'delete_snapshot', event_id: eventId, snapshot_id: snapshot.id,
    });
    if (!result.ok) { toast.error(result.message || 'Could not remove that snapshot'); return; }
    setSnapshots((prev) => prev.filter((s) => s.id !== snapshot.id));
  };

  // Refresh history whenever the schedule changes underneath us.
  useEffect(() => {
    if (!eventId) return;
    void fetchSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, data?.schedule_version?.id, gridRefreshToken]);

  const handleRegenerate = async () => {


    setRegenerating(true);
    try {
      const result = await invokeSchedule({ action: 'generate', event_id: eventId });
      if (!result.ok) {
        toast.error(result.message || 'Generation failed');
      } else {
        const s = result.data.generation_summary;
        const levelsNote = s?.used_levels ? ' (grouped by level)' : '';
        toast.success(`Generated ${s?.total_pools ?? '?'} pools, ${s?.total_games ?? '?'} games across ${s?.divisions?.length ?? '?'} divisions${levelsNote}.`);
        setData(result.data);
        setRegenOpen(false);
        afterDialogClose(() => {
          fetchDetail();
          fetchPreflight();
          fetchTeamDelta(); fetchRosterImpact();
        });
      }
    } finally { setRegenerating(false); }
  };

  type PlacementPermit = { kind: 'confirmed'; confirmation: NonNullable<typeof placementConfirmation> };

  // This is intentionally unreachable from any toolbar control. A run needs a
  // permit created from the warning snapshot, or proof that its loaded scope is
  // non-empty and contains zero placed games.
  const handleAssignTimes = async (permit: PlacementPermit) => {
    if (assigning) return; // only guard: don't fire twice while in flight
    if (scopeConflict) { setPlacementError(scopeConflict); toast.error(scopeConflict); return; }
    const confirmation = permit.confirmation;
    const permitIsValid = placementConfirmation?.permit === confirmation.permit;
    if (!permitIsValid) {
      toast.error('Placement was not confirmed. Nothing changed.');
      return;
    }
    const placedBefore = confirmation.placed;
    setAssigning(true);
    setPlacementError(null);
    try {
      const result = await invokeSchedule({
        action: 'assign_slots',
        event_id: eventId,
        scope: confirmation.scope,
        order: confirmation.order,
        ignore_requests: confirmation.ignoreRequests,
        preserve_already_placed: confirmation.preserveAlreadyPlaced,

      });
      if (!result.ok) {
        const msg = result.message || 'Could not place games';
        setPlacementError(msg);
        toast.error(msg);
        return;
      }
      const s = result.data?.assignment_summary;
      setPlacement(s ?? null);
      try { await resolveVenueNames(s); } catch { /* names are a nicety */ }
      // Say what it did: how many are newly on the board, how many were
      // already on it and got moved, and how many are still unplaced.
      const assignedNow = Number(s?.assigned ?? 0);
      const preserved = Number(s?.preserved_placed ?? 0);
      const moved = confirmation.preserveAlreadyPlaced ? 0 : Math.min(placedBefore, assignedNow);
      const newlyPlaced = confirmation.preserveAlreadyPlaced ? assignedNow : Math.max(0, assignedNow - moved);
      const stillUnplaced = Number(s?.unassigned ?? 0);
      const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;
      toast.success(confirmation.preserveAlreadyPlaced
        ? `Placed ${plural(newlyPlaced, 'game')}. ${preserved} left where ${preserved === 1 ? 'it was' : 'they were'}.`
        : `Placed ${plural(newlyPlaced, 'game')}, moved ${moved}, left ${stillUnplaced} unplaced.`);

      if (result.data) setData(result.data);
      setGridRefreshToken((token) => token + 1);
      setPlacementConfirmation(null);
      afterDialogClose(() => {
        fetchDetail();
        fetchPreflight();
        fetchTeamDelta(); fetchRosterImpact();
      });
    } catch (e: any) {
      const msg = e?.message || 'Placement failed unexpectedly.';
      console.error('[schedule-draft] Place games failed', e);
      setPlacementError(msg);
      toast.error(msg);
    } finally {
      setAssigning(false);
      // Never sticky: a what-if must not survive into the next run.
      setIgnoreRequests(false);
    }
  };

  const makePlacementConfirmation = (preset: 'all' | 'top' | 'requests' = 'all') => {
    const games = preset === 'all' ? gamesInScope : filterWave(preset);
    const placed = games.filter(gameIsPlaced).length;
    const presetFilter = preset === 'top'
      ? 'games involving starred teams only'
      : preset === 'requests' ? 'games with an approved request only' : null;
    const intoLine = (scopeCourts.length || windowLabel)
      ? `Into ${courtsLabel}${windowLabel ? ` — ${windowLabel}` : ''}`
      : null;
    return {
      permit: `${Date.now()}-${Math.random()}`,
      placed,
      unplaced: games.length - placed,
      scoped: games.length,
      handEdited: games.filter((g) => g?.manually_adjusted || g?.locked).length,
      locked: games.filter((g) => g?.locked).length,
      eventTotal: allDraftGames.length,
      narrowed: scopeIsNarrowed || preset !== 'all',
      scopeLabel,
      division: scopeDivision,
      filters: [filtersInForceLine, presetFilter].filter(Boolean).join(', '),
      preset,
      intoLine,
      waveSlots: waveSlotCount(),
      scope: {
        date: scopeDate === 'all' ? null : scopeDate,
        division: scopeDivision === 'all' ? null : scopeDivision,
        game_type: scopeGameType === 'all' ? null : scopeGameType,
        bracket_round: scopeRound === 'all' ? null : Number(scopeRound),
        start_date: scopeStartDate || null,
        start_time: (scopeStartDate || scopeEndTime) && scopeStartTime ? scopeStartTime : null,
        pools: scopePools,
        court_ids: scopeCourts,
        end_time: scopeEndTime || null,
        registration_ids: preset === 'all' ? [] : waveRegistrationIds(games),
      },
      order: placementOrderChoice,
      ignoreRequests,
      // Never sticky: every newly opened confirmation starts in the safe mode.
      preserveAlreadyPlaced: true,
    };
  };

  // The toolbar never invokes placement. It either opens the sole gate, or
  // opens the sole gate. Even an empty board shows the control disabled so the
  // director sees exactly what this run will do before it starts.
  const handlePlaceClick = (preset: 'all' | 'top' | 'requests' = 'all') => {
    if (assigning) return;
    if (preset === 'top' && starredGameCount === 0) {
      toast.error('No teams are starred yet. Star your top teams in Registration first.', {
        action: {
          label: 'Go to Registration',
          onClick: () => navigate(`/admin-dashboard/events/${eventId}/teams`),
        },
      });
      return;
    }
    if (preset === 'requests' && requestGameCount === 0) {
      toast.error('No games carry an approved scheduling request. Nothing changed.');
      return;
    }
    const confirmation = makePlacementConfirmation(preset);
    if (confirmation.scoped === 0) {
      toast.error('No games are in this scope. Nothing changed.');
      return;
    }
    setPlacementConfirmation(confirmation);
  };


  // missing_travel_pairs comes back as venue uuids — show building names instead.
  const resolveVenueNames = async (summary: any) => {
    const pairs = summary?.missing_travel_pairs ?? [];
    if (!pairs.length) return;
    const ids = [...new Set(pairs.flatMap((p: any) => [p.from_venue_id, p.to_venue_id]).filter(Boolean))];
    if (!ids.length) return;
    try {
      const { data: rows } = await (supabase as any).from('venues').select('id, name').in('id', ids);
      const map: Record<string, string> = {};
      for (const r of rows ?? []) map[r.id] = r.name;
      setVenueNames((prev) => ({ ...prev, ...map }));
    } catch { /* names are a nicety; uuids still render */ }
  };

  const handleApplyConstraints = async () => {
    setApplyingConstraints(true);
    try {
      const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
        body: { action: 'apply_constraints', event_id: eventId },
      });
      if (error || !res?.ok) {
        toast.error(error?.message || res?.error?.message || 'Failed to apply approved requests');
      } else {
        const d = res.data;
        if ((d?.constraints_total ?? 0) === 0) {
          toast.info('No approved scheduling requests to apply yet.');
        } else {
          toast.success(
            `Applied approved requests: ${d.constraints_honored}/${d.constraints_total} honored` +
            (d.games_moved ? `, ${d.games_moved} game${d.games_moved === 1 ? '' : 's'} moved.` : '.'),
          );
        }
        fetchDraft();
        fetchDetail();
      }
    } finally { setApplyingConstraints(false); }
  };

  // Hooks must stay above every loading/error/empty return. The previous
  // restructure placed these below those returns, so the completed data render
  // registered more hooks than the initial loading render (React error 310).
  const gridFull = view === 'grid' && !!data?.schedule_version;
  // The scheduling shell owns the pane now — see SchedulingPageShell.

  // Changing a control inside the dialog re-reads the counts from the same set
  // the run will act on, so the sentence never describes a different scope.
  useEffect(() => {
    if (!placementConfirmation) return;
    setPlacementConfirmation((prev) => {
      if (!prev) return prev;
      const next = makePlacementConfirmation(prev.preset as 'all' | 'top' | 'requests');
      return { ...next, preserveAlreadyPlaced: prev.preserveAlreadyPlaced };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeDate, scopeDivision, scopeGameType, scopeRound, scopeStartDate, scopeStartTime,
      scopeEndTime, scopePools, scopeCourts, placementOrderChoice, ignoreRequests]);

  if (loading) return (
    <div className="p-6 space-y-4">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
  if (error) return (
    <div className="p-6 space-y-4">
      <button
        onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}
        className="flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" /> Event overview
      </button>
      {publishStatus?.published && (
        <div className="rounded-lg border border-green-300 bg-green-50/60 p-3 flex items-center justify-between gap-3 flex-wrap text-sm">
          <span className="text-green-900 flex items-center gap-2">
            <Globe className="h-4 w-4 text-green-600" />
            Schedule <strong>published</strong>
            {publishStatus.schedule_published_at && ` on ${new Date(publishStatus.schedule_published_at).toLocaleString()}`}.
          </span>
          <Button size="sm" variant="outline" onClick={() => setUnpublishOpen(true)}>
            <EyeOff className="h-4 w-4 mr-2" /> Unpublish
          </Button>
        </div>
      )}
      <div className="text-sm text-destructive">{error}</div>

      <AlertDialog open={unpublishOpen} onOpenChange={setUnpublishOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              The public event page will show "Schedule Coming Soon" again and the schedule becomes editable. You can republish after making changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unpublishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleUnpublish(); }} disabled={unpublishing}>
              {unpublishing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Unpublish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
  if (!data) return null;


  const { event, schedule_version, pools_by_division } = data;
  const usesLevels = !!event?.registration_levels_enabled;

  const ReadinessChecklist = () => (
    <Card className="border-amber-200 bg-amber-50/40">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Finish setup before generating
            </h2>
            <p className="text-sm text-muted-foreground">
              {metCount}/{requirements.length} prerequisites complete
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => goToSetup()}>
            <Settings className="h-4 w-4 mr-2" /> Open schedule setup
          </Button>
        </div>
        <ul className="mt-4 space-y-2">
          {requirements.map((r) => (
            <li key={r.label} className="flex items-center gap-2 text-sm">
              {r.met ? (
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground/60 shrink-0" />
              )}
              <span className={r.met ? 'text-muted-foreground line-through' : ''}>{r.label}</span>
              {!r.met && (
                <button
                  onClick={() => goToSetup(r.section)}
                  className="ml-auto text-xs text-primary hover:underline shrink-0"
                >
                  Add now
                </button>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );

  // The wave controls live in the Place games dialog now — that is where he
  // picks the scope anyway, and the board keeps its pixels.
  const waveControls = (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 p-3">
            {/* Scope: which games the next run may move. Default = everything. */}
            <div className="flex flex-wrap items-center gap-1 min-w-0">
              <Select value={scopeDate} onValueChange={setScopeDate}>
                <SelectTrigger className="h-9 w-[150px]" aria-label="Day scope">
                  <SelectValue placeholder="All days" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All days</SelectItem>
                  {scopeDateOptions.map((d) => (
                    <SelectItem key={d} value={d}>{formatScopeDate(d)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={scopeDivision} onValueChange={setScopeDivision}>
                <SelectTrigger className="h-9 w-[140px]" aria-label="Division scope">
                  <SelectValue placeholder="All divisions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All divisions</SelectItem>
                  {scopeDivisionOptions.map((d: string) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={scopeGameType} onValueChange={setScopeGameType}>
                <SelectTrigger className="h-9 w-[130px]" aria-label="Game type scope">
                  <SelectValue placeholder="All games" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All games</SelectItem>
                  <SelectItem value="pool">Pool only</SelectItem>
                  <SelectItem value="bracket">Bracket &amp; consolation</SelectItem>
                </SelectContent>
              </Select>
              {bracketRounds.length > 0 && (
                <Select value={scopeRound} onValueChange={setScopeRound}>
                  <SelectTrigger className="h-9 w-[160px]" aria-label="Round scope">
                    <SelectValue placeholder="All rounds" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All rounds</SelectItem>
                    {bracketRounds.map((r) => (
                      <SelectItem key={r} value={String(r)}>{roundLabel(r)} (bracket only)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {/* The wave: which pools, onto which courts. Empty = all. */}
              {poolScopeOptions.length > 0 && (
                <WavePicker
                  options={poolScopeOptions.map((p) => ({ value: p, label: p }))}
                  selected={scopePools}
                  onChange={setScopePools}
                  allLabel="All pools"
                  noun="pool"
                />
              )}
              {courtScopeOptions.length > 0 && (
                <WavePicker
                  options={courtScopeOptions.map((c) => ({ value: c.id, label: c.name }))}
                  selected={scopeCourts}
                  onChange={setScopeCourts}
                  allLabel="All courts"
                  noun="court"
                />
              )}
              {/* Start from: the Saturday-afternoon control. Games and slots
                  before this moment are left exactly as they are. */}
              <Input
                type="date"
                value={scopeStartDate}
                onChange={(e) => setScopeStartDate(e.target.value)}
                className="h-9 w-[150px]"
                aria-label="Start from date"
                title="Start from — nothing before this moment is moved, and no game is placed before it"
              />
              <Input
                type="time"
                value={scopeStartTime}
                onChange={(e) => setScopeStartTime(e.target.value)}
                className="h-9 w-[120px]"
                aria-label="Start from time"
                title="The time this wave opens"
              />
              <Input
                type="time"
                value={scopeEndTime}
                onChange={(e) => setScopeEndTime(e.target.value)}
                className="h-9 w-[120px]"
                aria-label="To time"
                title="The time this wave closes — no game is placed to finish after it"
              />

              <Select value={placementOrderChoice} onValueChange={setPlacementOrderChoice}>
                <SelectTrigger className="h-9 w-[190px]" aria-label="Placement order">
                  <SelectValue placeholder="Order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="most_restrictive">Most restrictive first</SelectItem>
                  <SelectItem value="as_generated">As generated</SelectItem>
                  <SelectItem value="by_pool">By pool</SelectItem>
                  <SelectItem value="by_round">By round</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scopeConflict && (
              <span className="text-xs text-amber-700 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> {scopeConflict}
              </span>
            )}

            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none"
              title="A what-if run: the placer fills the grid without honouring any scheduling request. No refund recommendations are created. Resets after every run.">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-amber-600"
                checked={ignoreRequests}
                onChange={(e) => setIgnoreRequests(e.target.checked)}
                disabled={assigning}
              />
              Ignore scheduling requests for this run
            </label>
    </div>
  );

  // The one Place games control on the page: the button, and behind its
  // chevron the presets and Start over (a whole-draft rebuild, not an
  // everyday button).
  const placeControl = (
    <div className="flex">
      <Button
        type="button"
        onClick={(e) => { e.preventDefault(); handlePlaceClick('all'); }}
        size="sm"
        variant={(data?.unscheduled_count ?? 0) > 0 ? 'default' : 'outline'}
        disabled={assigning || !!placementBlocker || !!scopeConflict}
        className="rounded-r-none"
        title={placementBlocker?.message ?? scopeConflict ?? ((scopeLabel || startFromLabel)
          ? `Only ${scopeLabel || 'all'} games${startFromLabel ? ` ${startFromLabel}` : ''} may move — everything else keeps its court and time`
          : 'Place every game on a court and time slot')}
      >
        {assigning
          ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          : <CalendarClock className="h-4 w-4 mr-2" />}
        {assigning ? 'Placing games…' : placeButtonLabel}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant={(data?.unscheduled_count ?? 0) > 0 ? 'default' : 'outline'}
            disabled={assigning}
            className="rounded-l-none border-l px-2"
            aria-label="Other placement options"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64 bg-popover z-50">
          <DropdownMenuItem onSelect={() => handlePlaceClick('all')}>
            <CalendarClock className="h-4 w-4 mr-2" /> Place all games
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => handlePlaceClick('top')}>
            <Star className="h-4 w-4 mr-2" /> Place top teams
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => handlePlaceClick('requests')}>
            <ClipboardList className="h-4 w-4 mr-2" /> Place schedule requests first
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openRegen()} disabled={regenerating}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {schedule_version ? 'Start over — rebuild the draft' : 'Create pools & games'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const pageTitle =
    view === 'grid' ? 'Grid'
      : view === 'bulk' ? 'Bulk edit'
        : view === 'snapshots' ? 'Save / Restore'
          : 'Pools';

  const headerActions = (
    <>
      {schedule_version && data.total_games > 0 && (data.unscheduled_count ?? 0) < data.total_games && (
        <Button
          onClick={handleApplyConstraints}
          variant="outline"
          size="sm"
          disabled={applyingConstraints}
          title="Honor approved coach scheduling requests in game placement"
        >
          {applyingConstraints
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <CheckCircle2 className="h-4 w-4 mr-2" />}
          Apply approved requests
        </Button>
      )}
      {schedule_version && teamChanges > 0 && (
        <Button
          type="button"
          onClick={(e) => { e.preventDefault(); void handleSyncTeams(); }}
          variant="outline"
          size="sm"
          disabled={syncing}
          title="Add late registrations and remove withdrawn teams without rebuilding the schedule"
        >
          {syncing
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Users className="h-4 w-4 mr-2" />}
          {syncing ? 'Updating teams…' : syncButtonLabel}
        </Button>
      )}
      {/* On the Grid the Place games control lives in the board's control row,
          beside Save, History and Undo. One button, never two. */}
      {!gridFull && placeControl}
      {eventSlug && schedule_version && (
        <Button asChild variant="outline" size="sm" title="Open the public schedule for this draft in a new tab. Publishes nothing.">
          <a href={`/schedule/${eventSlug}?preview=draft`} target="_blank" rel="noreferrer" data-testid="schedule-preview-button">
            <Eye className="h-4 w-4 mr-2" /> Preview
          </a>
        </Button>
      )}
      {publishStatus?.published ? (
        <Button onClick={() => setUnpublishOpen(true)} variant="outline" size="sm">
          <EyeOff className="h-4 w-4 mr-2" /> Unpublish
        </Button>
      ) : (
        <Button
          onClick={() => setPublishOpen(true)}
          size="sm"
          disabled={!schedule_version || (data?.unscheduled_count ?? 0) === data?.total_games}
        >
          <Globe className="h-4 w-4 mr-2" /> Publish schedule
        </Button>

      )}
    </>
  );

  const headerSubtitle = (
    <span className="flex flex-wrap items-center gap-x-2">
      <span>{event?.name}</span>
      {schedule_version && (
        <span>
          · Draft generated {schedule_version.generated_at ? new Date(schedule_version.generated_at).toLocaleString() : '—'}
          {schedule_version.label && ` · ${schedule_version.label}`}
        </span>
      )}
      {schedule_version && teamChanges === 0 && <span>· No team changes</span>}
      <span>· {usesLevels
        ? 'Pools grouped by registration level.'
        : 'Levels disabled — pools grouped by division only.'}</span>
    </span>
  );

  return (
    <SchedulingPageShell
      title={pageTitle}
      subtitle={headerSubtitle}
      actions={headerActions}
      fill={gridFull}
    >
    <div className={gridFull ? 'schedule-grid-focus flex min-h-0 w-full flex-1 flex-col' : 'w-full space-y-6'}>
      <div className={gridFull ? 'hidden' : ''}>

        {/* Publish state banner */}
        {!gridFull && publishStatus && (
          <div className={`mt-4 rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap text-sm ${publishStatus.published ? 'border-green-300 bg-green-50/60' : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex items-center gap-2">
              {publishStatus.published ? (
                <>
                  <Globe className="h-4 w-4 text-green-600 shrink-0" />
                  <span className="text-green-900">
                    Schedule <strong>published</strong>
                    {publishStatus.schedule_published_at && ` on ${new Date(publishStatus.schedule_published_at).toLocaleString()}`}.
                    {' '}{publishStatus.game_numbers_changed_since_publish
                      ? 'Game numbers changed since it went live — printed sheets and anything a coach wrote down are now out of date. Republish to update the public page.'
                      : publishStatus.edited_since_publish
                        ? 'Edited since — republish to update the public page.'
                        : 'It is live on the public event page.'}
                  </span>
                </>
              ) : (
                <>
                  <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">This schedule is <strong>not published</strong>. The public event page shows "Schedule Coming Soon".</span>
                </>
              )}
            </div>
            {publishStatus.published && (publishStatus.has_newer_draft || publishStatus.edited_since_publish) && (
              <Button size="sm" variant="outline" onClick={() => setPublishOpen(true)}>
                <RefreshCw className="h-3.5 w-3.5 mr-2" /> Republish
              </Button>
            )}
          </div>
        )}

        {!gridFull && eventId && <ScheduleTabsEditor eventId={eventId} />}

        {/*
          The check before you publish — one UI everywhere. The counter is the
          way in; the panel behind it is the same component the board uses, so
          Bulk edit and the Grid never word the same finding two ways.
        */}
        {!gridFull && schedule_version && (
          <div className={`mt-4 rounded-lg border p-3 text-sm ${
            (check?.error_count ?? 0) > 0 ? 'border-red-300 bg-red-50/60'
              : (check?.warning_count ?? 0) > 0 ? 'border-amber-300 bg-amber-50/60'
              : 'border-slate-200 bg-slate-50'}`}>
            <div className="flex flex-wrap items-center gap-2">
              {(check?.error_count ?? 0) > 0
                ? <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />
                : (check?.warning_count ?? 0) > 0
                  ? <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                  : <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />}
              <span className={(check?.error_count ?? 0) > 0 ? 'text-red-900' : 'text-muted-foreground'}>
                {checking ? 'Checking the schedule…' : (check?.summary ?? 'Not checked yet')}
              </span>
              {((check?.error_count ?? 0) + (check?.warning_count ?? 0)) > 0 && (
                <button
                  type="button"
                  data-testid="check-counter"
                  className="ml-auto flex items-center gap-2 rounded border px-2 py-1 hover:bg-muted"
                  aria-label="See what is wrong with this schedule"
                  title="See what is wrong with this schedule"
                  onClick={() => setCheckOpen(true)}
                >
                  {(check?.error_count ?? 0) > 0 && (
                    <span className="flex items-center gap-1.5 font-medium text-red-700">
                      <Circle className="h-3.5 w-3.5 fill-red-600 text-red-700" />
                      {check.error_count} error{check.error_count === 1 ? '' : 's'}
                    </span>
                  )}
                  {(check?.warning_count ?? 0) > 0 && (
                    <span className="flex items-center gap-1.5 text-amber-700">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {check.warning_count} warning{check.warning_count === 1 ? '' : 's'}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {!gridFull && schedule_version && (
          <ScheduleCheckPanel
            open={checkOpen}
            onOpenChange={setCheckOpen}
            check={check}
            checking={checking}
            onRunCheck={() => void fetchCheck()}
            onFocusGame={(row) => focusGame(row)}
            onPublishAnyway={() => setOverrideOpen(true)}
            cellCount={(check?.error_count ?? 0) + (check?.warning_count ?? 0)}
            teamSummaryHref={(regId) =>
              `/admin-dashboard/events/${eventId}/scheduling/team-summary?team=${regId}`}
            setupHref={`/admin-dashboard/events/${eventId}/schedule-setup`}
          />
        )}


        {!gridFull && schedule_version && eventId && (
          <ScheduleToolsRow
            eventId={eventId}
            versionId={schedule_version?.id ?? null}
            isPublished={!!publishStatus?.published}
            onApplied={() => { void fetchDraft(); void fetchCheck(); }}
          />
        )}

        {/* Share the live results: QR + link, generated in the browser */}
        {!gridFull && publishStatus?.published && eventSlug && (
          <div className="mt-4">
            <PublicResultsQR
              url={`${window.location.origin}/schedule/${eventSlug}`}
              label="Live results — schedule, scores, standings"
            />
          </div>
        )}
      </div>


      {/* Placement — pre-flight warnings and the result of the last run */}
      {(placementBlocker || placement || placementError || unpooledWarning || capacityWarning) && (
        <Card>
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-base font-semibold">Placement</h2>
            </div>

            {[
              unpooledNotice,
              capacityWarning ? { tone: 'warn' as const, text: capacityWarning } : null,
            ]
              .filter(Boolean)
              .map((n: any, i: number) =>
                n.tone === 'info' ? (
                  <div
                    key={i}
                    data-testid="placement-no-pools-notice"
                    className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground"
                  >
                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{n.text}</span>
                  </div>
                ) : (
                  <div
                    key={i}
                    className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm text-amber-900"
                  >
                    <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                    <span>{n.text}</span>
                  </div>
                )
              )}

            {placementError && (
              <div className="flex items-start justify-between gap-3 flex-wrap rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm">
                <span className="text-destructive flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  Placement failed: {placementError}
                </span>
                <Button size="sm" variant="outline" onClick={() => handlePlaceClick('all')} disabled={assigning}>
                  Try again
                </Button>
              </div>
            )}





            {placementBlocker ? (
              <div className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-sm">
                <span className="text-amber-900 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
                  {placementBlocker.message}
                </span>
                <Button size="sm" variant="outline" onClick={placementBlocker.go}>
                  {placementBlocker.cta} <ExternalLink className="h-3.5 w-3.5 ml-2" />
                </Button>
              </div>
            ) : placement ? (() => {
              const p = placement;
              const unassigned = p?.unassigned ?? 0;
              const blocked = (p?.travel_blocked ?? 0) > 0 || (p?.rest_blocked ?? 0) > 0
                || (p?.daily_cap_blocked ?? 0) > 0 || (p?.missing_travel_pairs?.length ?? 0) > 0;
              const clean = unassigned === 0 && !blocked;
              const ranScope = p?.scope?.scoped
                ? [
                    p.scope.date ? formatScopeDate(p.scope.date) : null,
                    p.scope.division ?? null,
                    p.scope.bracket_round != null
                      ? `${roundLabel(Number(p.scope.bracket_round))} (bracket)`
                      : (p.scope.game_type ? `${p.scope.game_type} games` : null),
                    p.scope.start_date
                      ? `from ${formatScopeDate(p.scope.start_date)}${p.scope.start_time ? ` ${formatTime(p.scope.start_time)}` : ''}`
                      : null,
                  ].filter(Boolean).join(', ')
                : '';


              const vName = (id: string) => venueNames[id] || 'another venue';
              return (
                <div className="space-y-2 text-sm">
                  <div className={clean ? 'text-green-700 font-medium flex items-center gap-2' : 'font-medium'}>
                    {clean && <CheckCircle2 className="h-4 w-4" />}
                    {ranScope
                      ? `Placed ${p?.assigned ?? 0} of ${p?.games_in_scope ?? 0} games for ${ranScope}.`
                      : `${p?.assigned ?? 0} of ${draftGameCount || p?.total_games || 0} games placed.`}
                  </div>
                  <div className="text-muted-foreground">
                    Order used: {PLACEMENT_ORDER_LABELS[p?.order ?? 'as_generated'] ?? 'As generated'}.
                  </div>

                  {p?.ignored_requests && (
                    <div className="rounded border border-amber-300 bg-amber-50 p-2 text-amber-900">
                      Placed ignoring scheduling requests.{' '}
                      {(p?.paid_requests_violated ?? 0) > 0
                        ? `${p.paid_requests_violated} approved request${p.paid_requests_violated === 1 ? '' : 's'} a coach paid for ${p.paid_requests_violated === 1 ? 'is' : 'are'} violated by this schedule. No refund recommendations were created for this run.`
                        : 'No paid request is violated by the resulting schedule.'}
                    </div>
                  )}

                  {/* Maximum gap is a preference: it never leaves a game unplaced,
                      so this is reported whether or not the run was clean. */}
                  {(p?.max_gap_exceeded ?? 0) > 0 && (
                    <div className="text-amber-800">
                      {p.max_gap_exceeded} game{p.max_gap_exceeded === 1 ? '' : 's'} placed with a longer gap than you wanted
                      {p?.max_gap_minutes ? ` (maximum gap ${p.max_gap_minutes} min)` : ''} — the alternative was leaving them unplaced.
                    </div>
                  )}

                  {(p?.over_max_gap_pairs ?? 0) > 0 && (
                    <div className="text-muted-foreground">
                      {p.over_max_gap_pairs} team wait{p.over_max_gap_pairs === 1 ? '' : 's'} in the finished schedule exceed the maximum gap.
                    </div>
                  )}





                  {!clean && (
                    <>
                      {(p?.requests_honored ?? 0) + (p?.requests_not_honored ?? 0) > 0 && (
                        <div className="text-muted-foreground">
                          {p.requests_honored} scheduling request{p.requests_honored === 1 ? '' : 's'} honored
                          {p.requests_not_honored > 0 && `, ${p.requests_not_honored} could not be`}
                          {(p.soft_fallbacks ?? 0) > 0 && ` · ${p.soft_fallbacks} soft request${p.soft_fallbacks === 1 ? '' : 's'} gave way.`}
                        </div>
                      )}

                      {(p?.venues_used ?? 0) > 1 && (
                        <div className="text-muted-foreground">
                          {p.venues_used} venues used · {p.cross_venue_moves ?? 0} team{p.cross_venue_moves === 1 ? '' : 's'} moved between buildings.
                        </div>
                      )}

                      {(p?.travel_blocked ?? 0) > 0 && (
                        <div className="text-amber-800">
                          {p.travel_blocked} game{p.travel_blocked === 1 ? '' : 's'} could not be placed because of drive time between venues.
                        </div>
                      )}

                      {(p?.rest_blocked ?? 0) > 0 && (
                        <div className="text-amber-800">
                          {p.rest_blocked} game{p.rest_blocked === 1 ? '' : 's'} could not be placed because no slot gave both teams the minimum rest.
                        </div>
                      )}

                      {(p?.daily_cap_blocked ?? 0) > 0 && (
                        <div className="text-amber-800">
                          {p.daily_cap_blocked} game{p.daily_cap_blocked === 1 ? '' : 's'} could not be placed because a team had hit the daily game cap.
                        </div>
                      )}



                      {(p?.missing_travel_pairs?.length ?? 0) > 0 && (
                        <ul className="list-disc pl-5 text-amber-800 space-y-1">
                          {p.missing_travel_pairs.map((pair: any, i: number) => (
                            <li key={i}>
                              No driving time between {vName(pair.from_venue_id)} and {vName(pair.to_venue_id)} — games were not moved between them.
                            </li>
                          ))}
                        </ul>
                      )}

                      {unassigned > 0 && (
                        <div className="rounded-lg border bg-muted/40 p-3 text-muted-foreground">
                          <strong className="text-foreground">{unassigned} game{unassigned === 1 ? '' : 's'} still unplaced.</strong>{' '}
                          What usually makes them fit: more court hours (open courts earlier or later, or add a court),
                          a smaller venue travel buffer, or unlocking games that are pinned to a fixed court and time.
                          <div className="mt-2 flex gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => goToVenues('availability')}>
                              Court hours <ExternalLink className="h-3.5 w-3.5 ml-2" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => goToVenues('travel')}>
                              Travel times <ExternalLink className="h-3.5 w-3.5 ml-2" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })() : null}

          </CardContent>
        </Card>
      )}

      {/* Pools — per division membership, seeds and auto-balance.
          Divisions → Pools is the primary home for this; the combined view keeps it. */}
      {schedule_version && view === 'pools' && (
        <PoolsPanel
          eventId={eventId!}
          onChanged={() => {
            setGridRefreshToken((t) => t + 1);
            void fetchDraft();
          }}
        />
      )}

      {/* Brackets — per division, off the existing bracket engine. */}
      {schedule_version && view === 'pools' && (
        <BracketsPanel
          eventId={eventId!}
          versionId={schedule_version.id}
          canManage
          onChanged={() => {
            setGridRefreshToken((t) => t + 1);
            void fetchDraft();
          }}
        />
      )}

      {/* History — every automatic and saved arrangement for this version. */}
      {schedule_version && (view === 'pools' || view === 'snapshots') && snapshots.length > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-1">
              <History className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-base font-semibold">History</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Restoring puts games back where they were. It never changes pools, matchups or scores.
            </p>
            <ul className="divide-y">
              {snapshots.map((s) => (
                <li key={s.id} className="flex items-center gap-3 py-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      {s.label}
                      {s.reason === 'manual' && <Badge variant="secondary" className="text-[10px]">Saved</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(s.created_at).toLocaleString()} · {s.created_by_name} · {s.game_count} game{s.game_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => void openRestore(s)}
                    disabled={previewing || restoring}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-2" /> Restore
                  </Button>
                  {s.reason === 'manual' && (
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => void handleDeleteSnapshot(s)}
                      title="Remove this saved arrangement"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {syncSummary && (

        <Card>
          <CardContent className="space-y-2 py-4 text-sm">
            <div className="flex items-center gap-2 font-semibold">
              <Users className="h-4 w-4" /> Team update
            </div>
            {(syncSummary.teams_added ?? []).map((t: any, i: number) => (
              <div key={`a${i}`}>Added <strong>{t.name}</strong> to {t.division} {t.pool}.</div>
            ))}
            {(syncSummary.teams_removed ?? []).map((t: any, i: number) => (
              <div key={`r${i}`}>
                Removed <strong>{t.name}</strong>{t.pool ? ` from ${t.division ?? ''} ${t.pool}` : ''} and deleted their pool games.
              </div>
            ))}
            {(syncSummary.pools_created ?? []).length > 0 && (
              <div className="text-muted-foreground">
                New pools: {syncSummary.pools_created.map((p: any) => `${p.division} ${p.name}`).join(', ')}.
              </div>
            )}
            <div className="text-muted-foreground">
              {syncSummary.games_created ?? 0} game{(syncSummary.games_created ?? 0) === 1 ? '' : 's'} created,
              {' '}{syncSummary.games_deleted ?? 0} deleted. Existing games kept their court, time and flags.
            </div>
            {(syncSummary.bracket_games_needing_attention ?? []).length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-3 text-amber-900">
                <strong>{syncSummary.bracket_games_needing_attention.length} bracket game
                {syncSummary.bracket_games_needing_attention.length === 1 ? '' : 's'} need manual attention.</strong>{' '}
                Bracket games are never deleted automatically because they carry feeds and next-game links:
                {' '}
                {syncSummary.bracket_games_needing_attention
                  .map((b: any) => `${b.team}${b.label ? ` (${b.label})` : ''}`)
                  .join(', ')}.
              </div>
            )}
            {(syncSummary.teams_added ?? []).length === 0 && (syncSummary.teams_removed ?? []).length === 0 && (
              <div className="text-muted-foreground">Teams were already in sync — nothing changed.</div>
            )}
          </CardContent>
        </Card>
      )}




      {pendingRequests > 0 && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="py-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <ClipboardList className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="text-amber-900">
                <strong>{pendingRequests}</strong> pending scheduling request{pendingRequests === 1 ? '' : 's'} — review before finalizing.
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(`/admin-dashboard/events/${eventId}/scheduling-requests`)}
            >
              Review requests <ExternalLink className="h-3.5 w-3.5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      )}


      {!canGenerate && !prereqLoading && <ReadinessChecklist />}

      {/* The sidebar is the only navigation — the horizontal tab row is gone. */}

      {schedule_version && view === 'bulk' ? (
        <BulkEditGrid
          eventId={eventId!}
          refreshToken={gridRefreshToken}
          onChanged={() => {
            setGridRefreshToken((t) => t + 1);
            void fetchDraft();
          }}
        />
      ) : schedule_version && view === 'grid' ? (
        <div className={gridFull ? 'schedule-focus-board' : ''}>
          <div id="schedule-grid-anchor" />
          <ScheduleGrid
            eventId={eventId!}
            refreshToken={gridRefreshToken}
            focus={gridFocus}
            fillPane={gridFull}
            onPlaceGames={() => handlePlaceClick('all')}
            placeLabel={placeButtonLabel}
            placeDisabled={assigning || !!placementBlocker}
            onOpenHistory={() => setHistoryOpen(true)}
            onSaveArrangement={() => { setSaveLabel(''); setSaveOpen(true); }}
            placeControl={placeControl}
            onPublishAnyway={() => setOverrideOpen(true)}
            onUnplacedCount={setBoardUnplaced}
            unplacedReasons={Object.fromEntries(
              ((placement as any)?.unplaced_reasons ?? []).map((r: any) => [r.game_id, r.reason]),
            )}
          />
        </div>


      ) : !schedule_version ? (

        <Card>
          <CardContent className="py-10 flex flex-col items-center text-center gap-3">
            <Trophy className="h-10 w-10 text-muted-foreground" />
            <p className="font-medium">No draft schedule yet.</p>
            <p className="text-sm text-muted-foreground">Generate pools and games from your registered teams.</p>
            <Button onClick={openRegen} disabled={regenerating}>Generate pools</Button>
            {!canGenerate && !prereqLoading && (
              <p className="text-xs text-muted-foreground">
                Complete the {requirements.length - metCount} remaining prerequisite{requirements.length - metCount === 1 ? '' : 's'} above first.
              </p>
            )}
          </CardContent>
        </Card>
      ) : pools_by_division.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-sm text-muted-foreground">
            Draft created but no pools were generated. (No teams matched the registration filter.)
          </CardContent>
        </Card>
      ) : (
        pools_by_division.map((d: any) => (
          <Card key={d.division}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-2 mb-4">
                <h2 className="text-lg font-semibold">{d.division}</h2>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setSwapDivision({
                      division: d.division,
                      teams: d.pools.flatMap((p: any) =>
                        p.teams.map((t: any) => ({
                          registration_id:
                            detail?.teams_detail?.[`${d.division}|${p.name}|${t.seed}`]?.registration_id ?? '',
                          team_name: t.team_name,
                          pool: p.name,
                        })),
                      ),
                    })
                  }
                >
                  <ArrowLeftRight className="h-3.5 w-3.5 mr-1.5" /> Swap two teams
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {d.pools.map((p: any) => {
                  const isSingleton = p.teams.length === 1;
                  return (
                    <div key={p.name} className="rounded-lg border p-4">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{p.name}</span>
                          {p.level && <Badge variant="secondary">{p.level}</Badge>}
                          {isSingleton && (
                            <Badge variant="outline" className="text-amber-700 border-amber-300">
                              <AlertTriangle className="h-3 w-3 mr-1" /> Singleton — no games
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground text-right whitespace-nowrap">
                          <div>{p.teams.length} {p.teams.length === 1 ? 'team' : 'teams'}</div>
                          <div>{p.games.length} {p.games.length === 1 ? 'game' : 'games'}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                            <Users className="h-3 w-3" /> Teams
                          </div>
                          {p.teams.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No teams.</p>
                          ) : (
                            <ul className="space-y-1">
                              {p.teams.map((t: any) => {
                                const td = detail?.teams_detail?.[`${d.division}|${p.name}|${t.seed}`];
                                return (
                                  <li key={t.team_name} className="text-sm flex items-center gap-1 group">
                                    <span className="text-muted-foreground">{t.seed}.</span>
                                    <span className="min-w-0 truncate">{t.team_name}</span>
                                    <button
                                      type="button"
                                      title="Withdraw team from play"
                                      onClick={() => {
                                        setWithdrawTeam({
                                          team_name: t.team_name,
                                          division: d.division,
                                          registration_id: td?.registration_id ?? null,
                                        });
                                        setWithdrawOpen(true);
                                      }}
                                      className="ml-auto shrink-0 text-muted-foreground/50 hover:text-destructive transition-colors"
                                    >
                                      <UserMinus className="h-3.5 w-3.5" />
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Games</p>
                          {p.games.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No games (single-team pool).</p>
                          ) : (
                            <ul className="space-y-1">
                              {p.games.map((g: any) => {
                                const gd = detail?.games_detail?.[g.id];
                                return (
                                  <li
                                    key={g.id}
                                    className="flex items-center justify-between gap-2 py-1 border-b last:border-b-0 border-slate-100 -mx-1 px-1 rounded hover:bg-accent/50 cursor-pointer"
                                    onClick={() => openEditGame(g)}
                                    title="Edit game"
                                  >
                                    <div className="min-w-0">
                                      {(() => {
                                        const homeId = gd?.home_team_registration_id ?? gd?.team_a_registration_id;
                                        const aIsHome = !gd?.team_a_registration_id || homeId === gd?.team_a_registration_id;
                                        const tag = (isHome: boolean) => (
                                          <span className={`text-[9px] font-bold px-1 rounded ${isHome ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                            {isHome ? 'HOME' : 'AWAY'}
                                          </span>
                                        );
                                        return (
                                          <div className="text-sm flex items-center gap-1.5 flex-wrap">
                                            <span className="truncate inline-flex items-center gap-1">{tag(aIsHome)} {g.team_a_name}</span>
                                            <span className="text-slate-400">vs</span>
                                            <span className="truncate inline-flex items-center gap-1">{tag(!aIsHome)} {g.team_b_name}</span>
                                            <Pencil className="h-3 w-3 text-muted-foreground/40 shrink-0" />
                                          </div>
                                        );
                                      })()}

                                      {g.scheduled_start_time && g.scheduled_date && (
                                        <div className="text-xs text-slate-500 mt-0.5">
                                          {new Date(g.scheduled_date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                          {' · '}
                                          {formatTimeRange(g.scheduled_start_time, g.scheduled_end_time)}
                                          {g.court_name && <> · {g.court_name}</>}
                                        </div>
                                      )}
                                      {(gd?.manually_adjusted || gd?.locked) && (
                                        <div className="flex items-center gap-1 mt-0.5">
                                          {gd?.manually_adjusted && (
                                            <Badge variant="outline" className="text-[10px] py-0 h-4 text-amber-700 border-amber-300">
                                              Manually adjusted
                                            </Badge>
                                          )}
                                          {gd?.locked && (
                                            <Badge variant="outline" className="text-[10px] py-0 h-4 text-blue-700 border-blue-300">
                                              <Lock className="h-2.5 w-2.5 mr-0.5" /> Locked
                                            </Badge>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <button
                                        type="button"
                                        title="Flip home / away"
                                        onClick={(e) => { e.stopPropagation(); flipHomeAway(g.id); }}
                                        className="text-muted-foreground/50 hover:text-foreground transition-colors"
                                      >
                                        <ArrowLeftRight className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        title={gd?.locked ? 'Unlock game' : 'Lock game (bulk reassign will skip it)'}
                                        onClick={(e) => { e.stopPropagation(); toggleLock(g.id, !gd?.locked); }}
                                        className="text-muted-foreground/50 hover:text-foreground transition-colors"
                                      >
                                        {gd?.locked ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
                                      </button>

                                      <Badge variant={g.game_status === 'scheduled' ? 'default' : 'secondary'} className="text-xs capitalize">
                                        {g.game_status?.replace(/_/g, ' ')}
                                      </Badge>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Schedule quality summary — measured from the current draft */}
      {view !== 'bulk' && quality && (() => {
        const score = quality.quality_score ?? 0;
        const scoreColor =
          score >= 85 ? 'text-green-600' : score >= 65 ? 'text-amber-600' : 'text-red-600';
        const scoreLabel =
          score >= 85 ? 'Excellent' : score >= 65 ? 'Good' : 'Needs attention';
        return (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <Gauge className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-base font-semibold">Schedule quality</h2>
                <span className={`ml-auto text-sm font-medium ${scoreColor}`}>{scoreLabel}</span>
              </div>
              <div className="grid gap-4 sm:grid-cols-4">
                <div className="rounded-lg border p-3">
                  <div className="text-xs text-muted-foreground mb-1">Overall score</div>
                  <div className={`text-2xl font-bold ${scoreColor}`}>{score}<span className="text-base font-normal text-muted-foreground">/100</span></div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {quality.assigned_games}/{draftGameCount} games assigned
                  </div>
                </div>
                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    <Scale className="h-3.5 w-3.5" /> Games balance
                  </div>
                  <div className="text-2xl font-bold">{quality.games_balance?.pct ?? 0}%</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {quality.games_balance?.min_games}–{quality.games_balance?.max_games} games/team
                    {quality.games_balance?.spread > 0 ? ` · spread ${quality.games_balance.spread}` : ' · even'}
                  </div>
                </div>
                {(() => {
                  // One definition of back-to-back: the placer's count when we
                  // have a fresh run, otherwise the same threshold-based figure
                  // from quality_summary.
                  const b2b = placement?.back_to_back_pairs ?? quality.rest?.violations ?? 0;
                  return (
                    <div className="rounded-lg border p-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                        <Timer className="h-3.5 w-3.5" /> Rest
                      </div>
                      <div className={`text-2xl font-bold ${b2b > 0 ? 'text-amber-600' : ''}`}>{b2b}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        back-to-back pair{b2b === 1 ? '' : 's'}
                      </div>
                    </div>
                  );
                })()}

                <div className="rounded-lg border p-3">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Constraints honored
                  </div>
                  {quality.constraints?.pct != null ? (
                    <>
                      <div className="text-2xl font-bold">{quality.constraints.pct}%</div>
                      <div className="text-[11px] text-muted-foreground mt-1">
                        {quality.constraints.honored} of {quality.constraints.total} approved honored
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-2xl font-bold text-muted-foreground">0 of 0</div>
                      <div className="text-[11px] text-muted-foreground mt-1">— no approved requests yet</div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Short pools. Hidden entirely when every team meets the target. */}
      {view !== 'bulk' && rosterImpact && !rosterImpact.healthy && (
        <Card className="border-amber-300">
          <CardContent className="pt-6 space-y-5">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <h2 className="text-base font-semibold">Some teams are short of games</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              {(() => {
                // The promise can differ per division (a division override on
                // games_per_team_target). Never state one event-wide number
                // when the divisions below disagree with it.
                const targets = [...new Set((rosterImpact.divisions ?? []).map((d: any) => d.target))];
                if (targets.length === 1) {
                  const t = targets[0] as number;
                  return `This event promises ${t} game${t === 1 ? '' : 's'} per team.`;
                }
                return `Each division promises its own number of games per team: ${(rosterImpact.divisions ?? [])
                  .map((d: any) => `${d.division} ${d.target}`)
                  .join(', ')}.`;
              })()}
              {' '}Nothing below changes until you pick a fix, and each one shows exactly what it costs.
            </p>


            {rosterImpact.divisions.map((div: any) => (
              <div key={div.division} className="rounded-lg border p-4 space-y-4">
                <div>
                  <div className="font-semibold text-sm">{div.headline}</div>
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {div.pools.map((p: any) => (
                      <div key={p.pool_id}>
                        <strong className="text-foreground">{p.pool_name}</strong> — {p.team_count} team
                        {p.team_count === 1 ? '' : 's'}:{' '}
                        {p.teams.map((t: any) => `${t.name} (${t.games})`).join(', ')}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  {div.remedies.map((r: any) => (
                    <div
                      key={r.id}
                      className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${r.blocked ? 'opacity-70' : ''}`}
                    >
                      <div className="text-sm space-y-1">
                        <div className="font-medium">{r.label}</div>
                        <div className="text-muted-foreground">{r.detail}</div>
                        <div className="text-xs text-muted-foreground">
                          {r.cost.games_created > 0 && <>Adds {r.cost.games_created} game{r.cost.games_created === 1 ? '' : 's'}. </>}
                          {r.cost.games_deleted > 0
                            ? <>Deletes {r.cost.games_deleted} game{r.cost.games_deleted === 1 ? '' : 's'}
                                {r.cost.placed_games_deleted > 0 && <> — {r.cost.placed_games_deleted} already placed</>}
                                {r.cost.hand_edited_games_deleted > 0 && <>, {r.cost.hand_edited_games_deleted} hand-adjusted or locked</>}.</>
                            : <>Deletes nothing.</>}
                        </div>
                        {r.blocked && <div className="text-xs text-amber-700">{r.blocked}</div>}
                      </div>
                      <Button
                        size="sm"
                        variant={r.op === 'accept' ? 'outline' : 'default'}
                        disabled={Boolean(r.blocked) || applyingRemedy !== null}
                        onClick={() => void handleApplyRemedy(r)}
                      >
                        {applyingRemedy === r.id
                          ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Working</>
                          : r.op === 'accept' ? 'Draft the note' : 'Apply'}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {coachMessages && coachMessages.length > 0 && (
              <div className="rounded-lg border bg-muted/40 p-4 space-y-3">
                <div className="text-sm font-semibold">Draft notes — copy and send yourself</div>
                {coachMessages.map((m, i) => (
                  <div key={i} className="space-y-1">
                    <div className="text-xs font-medium text-muted-foreground">{m.team}</div>
                    <textarea
                      readOnly
                      className="w-full rounded-md border bg-background p-2 text-sm"
                      rows={4}
                      value={m.message}
                    />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summaries sit below the board: they are summaries, not controls. */}
      {view !== 'bulk' && schedule_version && outcomes.length > 0 && (() => {
        const honored = outcomes.filter((o) => o.honored);
        const violated = outcomes.filter((o) => !o.honored);
        const labelFor = (o: any) =>
          (o.constraint_type || 'constraint').replace(/_/g, ' ');
        return (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <h2 className="text-base font-semibold">Approved constraints in this draft</h2>
                <div className="flex items-center gap-3 text-sm">
                  <span className="inline-flex items-center gap-1 text-green-700">
                    <CheckCircle2 className="h-4 w-4" /> {honored.length} honored
                  </span>
                  <span className="inline-flex items-center gap-1 text-red-700">
                    <XCircle className="h-4 w-4" /> {violated.length} violated
                  </span>
                </div>
              </div>
              {violated.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Violated</p>
                  <ul className="space-y-1">
                    {violated.map((o) => (
                      <li key={o.id} className="text-sm flex items-start gap-2">
                        <XCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                        <span className="capitalize">{labelFor(o)}</span>
                        {o.scheduling_tier && (
                          <Badge variant="outline" className="text-[10px] capitalize">{String(o.scheduling_tier).replace(/_/g, ' ')}</Badge>
                        )}
                        {o.reason && <span className="text-muted-foreground normal-case">— {o.reason}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {honored.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Honored</p>
                  <ul className="space-y-1">
                    {honored.map((o) => (
                      <li key={o.id} className="text-sm flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                        <span className="capitalize">{labelFor(o)}</span>
                        {o.scheduling_tier && (
                          <Badge variant="outline" className="text-[10px] capitalize">{String(o.scheduling_tier).replace(/_/g, ' ')}</Badge>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <button
                onClick={() => navigate(`/admin-dashboard/events/${eventId}/scheduling-requests`)}
                className="mt-3 text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                Open Scheduling Requests inbox <ExternalLink className="h-3 w-3" />
              </button>
            </CardContent>
          </Card>
        );
      })()}

      <ScheduleHistoryPanel
        eventId={eventId!}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        snapshots={snapshots}
        onRestore={(snap) => { setHistoryOpen(false); void openRestore(snap); }}
        refreshToken={gridRefreshToken}
      />

      {/* Save this arrangement */}
      <AlertDialog open={saveOpen} onOpenChange={setSaveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save this arrangement</AlertDialogTitle>
            <AlertDialogDescription>
              Records where every game sits right now, so you can come back to it later.
              Saved arrangements are kept until you remove them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            autoFocus
            value={saveLabel}
            onChange={(e) => setSaveLabel(e.target.value)}
            placeholder="Saturday morning, before moving 14U"
            maxLength={120}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={savingSnapshot}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); void handleSaveSnapshot(); }} disabled={savingSnapshot}>
              {savingSnapshot && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore — always shows the dry run first */}
      <AlertDialog
        open={!!restoreTarget}
        onOpenChange={(open) => { if (!open && !restoring) { setRestoreTarget(null); setRestorePreview(null); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Restore "{restoreTarget?.label}" — {restoreTarget?.game_count ?? 0} game{restoreTarget?.game_count === 1 ? '' : 's'}
              {restoreTarget?.created_at ? `, saved ${new Date(restoreTarget.created_at).toLocaleString()}` : ''}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {previewing && (
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Working out what would change…</span>
                )}
                {restorePreview && (
                  <>
                    <div>
                      <strong>{restorePreview.games_moving}</strong> game{restorePreview.games_moving === 1 ? '' : 's'} would move,
                      {' '}<strong>{restorePreview.games_already_in_position}</strong> already in that position.
                    </div>
                    {restorePreview.games_not_in_snapshot > 0 && (
                      <div>
                        <strong>{restorePreview.games_not_in_snapshot}</strong> game
                        {restorePreview.games_not_in_snapshot === 1 ? ' was' : 's were'} added since and
                        {restorePreview.games_not_in_snapshot === 1 ? ' is' : ' are'} not in this snapshot — they keep their current slot.
                      </div>
                    )}
                    {restorePreview.games_missing_from_schedule > 0 && (
                      <div className="text-muted-foreground">
                        {restorePreview.games_missing_from_schedule} game
                        {restorePreview.games_missing_from_schedule === 1 ? '' : 's'} in the snapshot no longer exist and are skipped.
                      </div>
                    )}
                    {restorePreview.games_unplaced_outside_hours?.length > 0 && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-2 text-amber-900">
                        These sat in hours the court no longer has, so they come back unplaced:{' '}
                        {restorePreview.games_unplaced_outside_hours.map((g: any) => g.label).join(', ')}.
                      </div>
                    )}
                    {restorePreview.games_unplaced_by_collision?.length > 0 && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-2 text-amber-900">
                        These clash with a restored game and come back unplaced:{' '}
                        {restorePreview.games_unplaced_by_collision.map((g: any) => g.label).join(', ')}.
                      </div>
                    )}
                    {restorePreview.games_unplaced_by_result?.length > 0 && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50/60 p-2 text-amber-900">
                        A game that has already been played sits in these slots, so they come back unplaced:{' '}
                        {restorePreview.games_unplaced_by_result.map((g: any) => g.label).join(', ')}.
                      </div>
                    )}
                    {restorePreview.games_with_results_kept > 0 && (
                      <div>
                        <strong>{restorePreview.games_with_results_kept}</strong> game
                        {restorePreview.games_with_results_kept === 1 ? ' has' : 's have'} a score and stay
                        exactly where {restorePreview.games_with_results_kept === 1 ? 'it is' : 'they are'}
                        {restorePreview.games_kept_for_result?.length
                          ? `: ${restorePreview.games_kept_for_result.map((g: any) => g.label).join(', ')}`
                          : ''}.
                      </div>
                    )}

                    {restorePreview.published && (
                      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-2">
                        This schedule is live on the public event page — coaches and families will see these
                        times change as soon as you restore.
                      </div>
                    )}
                    <div className="text-muted-foreground">
                      Pools, matchups and scores are untouched. The current arrangement is saved first, so you can undo this too.
                    </div>

                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleRestore(); }}
              disabled={restoring || previewing || !restorePreview}
            >
              {restoring && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Restore
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={regenOpen} onOpenChange={setRegenOpen}>

        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start over?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the current draft and rebuilds every pool and game from scratch.
              {' '}
              {(preflight?.placedGames ?? 0) > 0 && (
                <>{preflight?.placedGames} placed game{preflight?.placedGames === 1 ? '' : 's'}</>
              )}
              {(preflight?.placedGames ?? 0) > 0 && (preflight?.adjustedGames ?? 0) > 0 && ' and '}
              {(preflight?.adjustedGames ?? 0) > 0 && (
                <>{preflight?.adjustedGames} hand-adjusted game{preflight?.adjustedGames === 1 ? '' : 's'}</>
              )}
              {startOverLoss > 0 && ' will be lost. '}
              To add teams who registered late, use Update teams instead.
              {' '}<strong>Start over cannot be undone</strong> — History only restores placements, not deleted pools and games.

            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus disabled={regenerating}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleRegenerate(); }} disabled={regenerating}>
              {regenerating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Start over
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <AlertDialog
        open={placementConfirmation !== null}
        onOpenChange={(open) => { if (!open && !assigning) setPlacementConfirmation(null); }}
      >
        <AlertDialogContent className="flex max-h-[85vh] flex-col gap-0 sm:max-w-2xl">
          <AlertDialogHeader className="shrink-0">
            <AlertDialogTitle>
              {placementConfirmation?.preset === 'requests'
                ? `Place ${placementConfirmation.scopeLabel ? `${placementConfirmation.scopeLabel} games` : 'games'} with an approved request?`
                : placementConfirmation?.preset === 'top'
                  ? `Place ${placementConfirmation.scopeLabel ? `${placementConfirmation.scopeLabel} games` : 'games'} involving starred teams?`
                  : placementConfirmation?.narrowed
                    ? placementConfirmation.scopeLabel
                      ? `Place ${placementConfirmation.scopeLabel} games?`
                      : 'Place games?'
                    : 'Place games across the whole event?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {placementConfirmation?.preserveAlreadyPlaced ? (
                  <p>
                    <strong>{placementConfirmation.placed} game{placementConfirmation.placed === 1 ? '' : 's'} stay where {placementConfirmation.placed === 1 ? 'it is' : 'they are'}.</strong>{' '}
                    {placementConfirmation.unplaced} game{placementConfirmation.unplaced === 1 ? ' will' : 's will'} be placed.
                  </p>
                ) : (
                  <p>
                    <strong>
                      {placementConfirmation?.placed ?? 0} game{placementConfirmation?.placed === 1 ? ' is' : 's are'} already on the board.
                    </strong>{' '}
                    Placing will move {placementConfirmation?.placed === 1 ? 'it' : 'them'}.{' '}
                    {placementConfirmation?.unplaced ?? 0} game{placementConfirmation?.unplaced === 1 ? ' is' : 's are'} not placed yet.
                  </p>
                )}
                {placementConfirmation?.narrowed && (
                  <p>
                    {placementConfirmation.division !== 'all' ? `${placementConfirmation.division} only — ` : ''}
                    {placementConfirmation.scoped} of the {placementConfirmation.eventTotal} games on this event are in scope.
                  </p>
                )}
                {/* The wave, in his own words: the courts and the window. */}
                {placementConfirmation?.intoLine && (
                  <p>{placementConfirmation.intoLine}.</p>
                )}
                <p>The filters in force: {placementConfirmation?.filters}.</p>
                {/* Say it before he commits, not afterwards. */}
                {(() => {
                  const slots = placementConfirmation?.waveSlots;
                  const candidates = placementConfirmation?.preserveAlreadyPlaced
                    ? (placementConfirmation?.unplaced ?? 0)
                    : Math.max(0, (placementConfirmation?.scoped ?? 0) - (placementConfirmation?.handEdited ?? 0));
                  if (slots == null || slots >= candidates) return null;
                  const short = candidates - slots;
                  return (
                    <p className="text-amber-700">
                      {candidates} game{candidates === 1 ? '' : 's'}, {slots} slot{slots === 1 ? '' : 's'} in
                      that window. {short} would be left unplaced.
                    </p>
                  );
                })()}

              </div>
            </AlertDialogDescription>
            {(() => {
              // Counted from the same in-scope set as the numbers above.
              const handEdited = placementConfirmation?.handEdited ?? 0;
              const lockedCount = placementConfirmation?.locked ?? 0;
              const rest = Math.max(0, (placementConfirmation?.scoped ?? 0) - handEdited);
              if (placementConfirmation?.preserveAlreadyPlaced || handEdited === 0) return null;
              return (
                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                  <div className="flex items-center gap-2 font-medium mb-1">
                    <AlertTriangle className="h-4 w-4" /> Manual edits present
                  </div>
                  {handEdited} game{handEdited === 1 ? ' has' : 's have'} been hand-edited
                  {lockedCount > 0 ? ` (${lockedCount} locked)` : ''}
                  {handEdited === 1 ? ' and keeps' : ' and keep'} their court and time.
                  {' '}The other {rest} game{rest === 1 ? '' : 's'} in scope will be re-placed.
                </div>
              );
            })()}
            <label className={`mt-3 flex items-start gap-3 rounded-md border p-3 text-sm ${placementConfirmation?.placed === 0 ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
              <Checkbox
                checked={placementConfirmation?.preserveAlreadyPlaced ?? true}
                disabled={assigning || (placementConfirmation?.placed ?? 0) === 0}
                onCheckedChange={(checked) => setPlacementConfirmation((current) => current
                  ? { ...current, preserveAlreadyPlaced: checked === true }
                  : current)}
                aria-label="Leave games that are already placed where they are"
              />
              <span>
                <strong>Leave games that are already placed where they are</strong>{' '}
                {(placementConfirmation?.placed ?? 0) === 0
                  ? 'Nothing in scope is placed yet.'
                  : 'Only empty slots get filled. Nothing on the board moves.'}
              </span>
            </label>
          </AlertDialogHeader>

          {/* The wave: which games, which courts, which hours. It lives here
              now instead of on the board — this is where he picks the scope,
              and the counts above move with it. */}
          <div className="min-h-0 flex-1 overflow-y-auto py-2">
            {waveControls}
          </div>

          <AlertDialogFooter className="shrink-0">
            <AlertDialogCancel autoFocus disabled={assigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (placementConfirmation) {
                  void handleAssignTimes({ kind: 'confirmed', confirmation: placementConfirmation });
                }
              }}
              disabled={assigning || !placementConfirmation}
            >
              {assigning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Place games
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={publishOpen} onOpenChange={setPublishOpen}>
        <AlertDialogContent data-testid="publish-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{publishStatus?.published ? 'Republish schedule?' : 'Publish schedule?'}</AlertDialogTitle>
            <AlertDialogDescription>
              This makes the current schedule live on the public event page, where teams and families can view games by court and time. You can unpublish at any time.
            </AlertDialogDescription>
            {(() => {
              // The board's own unplaced total (reported up by ScheduleGrid from
              // the same array the tray counts). The summary's unscheduled_count
              // is only a fallback for the moment before the grid has reported —
              // the two must never disagree once the board is on screen.
              const unplaced = boardUnplaced ?? data?.unscheduled_count ?? 0;
              const errors = check?.error_count ?? 0;
              if (errors <= 0 && unplaced <= 0) return null;
              return (
                <div
                  className="mt-2 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                  data-testid="publish-problem-line"
                >
                  {[
                    errors > 0 ? `${errors} ${errors === 1 ? 'error' : 'errors'}.` : null,
                    unplaced > 0 ? `${unplaced} ${unplaced === 1 ? 'game is' : 'games are'} not placed.` : null,
                    'Publishing now puts this schedule in front of teams and families with those problems in it.',
                  ].filter(Boolean).join(' ')}
                </div>
              );
            })()}
            {publishStatus?.published && (
              <div className="mt-2 rounded-md border p-3 text-sm" data-testid="publish-changes">
                {pubChangesLoading || !pubChanges ? (
                  <span className="text-muted-foreground">Checking what changed since you last published…</span>
                ) : pubChanges.change_count === 0 ? (
                  <span data-testid="publish-no-changes">No changes that affect a team's day, time or venue.</span>
                ) : (
                  <div className="space-y-2">
                    <p className="font-medium" data-testid="publish-change-count">
                      {pubChanges.change_count} {pubChanges.change_count === 1 ? 'change' : 'changes'} since you last published, affecting {pubChanges.affected_count} {pubChanges.affected_count === 1 ? 'team' : 'teams'}.
                    </p>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <Checkbox
                        checked={notifyChanges}
                        onCheckedChange={(v) => setNotifyChanges(v === true)}
                        data-testid="publish-notify-toggle"
                        className="mt-0.5"
                      />
                      <span>
                        <span className="font-medium">Notify the affected teams</span>
                        <span className="block text-muted-foreground" data-testid="publish-reach-line">
                          {pubChanges.affected_count} {pubChanges.affected_count === 1 ? 'team' : 'teams'} affected
                          {pubChanges.unreachable?.length > 0
                            ? ` · ${pubChanges.unreachable.length} ${pubChanges.unreachable.length === 1 ? 'has' : 'have'} no coach email and cannot be reached: ${pubChanges.unreachable.map((t: any) => t.name).join(', ')}`
                            : ''}
                        </span>
                        <span className="block text-muted-foreground">Court-only moves at the same time are not counted as changes.</span>
                      </span>
                    </label>
                  </div>
                )}
              </div>
            )}
            {(publishStatus?.pending_requests ?? 0) > 0 && (
              <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                <div className="flex items-center gap-2 font-medium mb-1">
                  <AlertTriangle className="h-4 w-4" /> Pending scheduling requests
                </div>
                {publishStatus.pending_requests} scheduling request{publishStatus.pending_requests === 1 ? '' : 's'} {publishStatus.pending_requests === 1 ? 'has' : 'have'} not been reviewed yet. Consider reviewing {publishStatus.pending_requests === 1 ? 'it' : 'them'} before publishing.
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
            {(check?.error_count ?? 0) > 0 ? (
              <>
                <Button
                  variant="outline"
                  disabled={publishing}
                  onClick={() => { setPublishOpen(false); setCheckOpen(true); }}
                >
                  Review the problems
                </Button>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); void handlePublish(true); }}
                  disabled={publishing}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {publishing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Publish anyway
                </AlertDialogAction>
              </>
            ) : (
              <AlertDialogAction onClick={(e) => { e.preventDefault(); void handlePublish(false); }} disabled={publishing}>
                {publishing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {publishStatus?.published ? 'Republish' : 'Publish'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>

      </AlertDialog>

      {/* Publish anyway — the errors are listed again, deliberately */}
      <AlertDialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publish with {check?.error_count ?? 0} unresolved problem{(check?.error_count ?? 0) === 1 ? '' : 's'}?</AlertDialogTitle>
            <AlertDialogDescription>
              These are physically impossible on the day. Publishing sends them to every team and family. Your name and the problems below are recorded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-56 overflow-y-auto space-y-1.5 text-sm">
            {check?.errors?.map((e: any, i: number) => (
              <li key={i} className="rounded border border-red-200 bg-red-50/60 p-2">
                <span className="font-medium text-red-800">{e.title}</span>
                <span className="text-muted-foreground"> — {e.message}</span>
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handlePublish(true); }}
              disabled={publishing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {publishing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Publish anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={unpublishOpen} onOpenChange={setUnpublishOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unpublish schedule?</AlertDialogTitle>
            <AlertDialogDescription>
              The public event page will show "Schedule Coming Soon" again and the schedule becomes editable. You can republish after making changes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unpublishing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleUnpublish(); }} disabled={unpublishing}>
              {unpublishing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Unpublish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>



      <EditGameModal
        open={editOpen}
        onOpenChange={setEditOpen}
        eventId={eventId!}
        game={editGame}
        detail={editGame ? detail?.games_detail?.[editGame.id] ?? null : null}
        courts={detail?.courts ?? []}
        sessions={detail?.sessions ?? []}
        onSaved={afterManualChange}
      />

      <WithdrawTeamDialog
        open={withdrawOpen}
        onOpenChange={setWithdrawOpen}
        eventId={eventId!}
        team={withdrawTeam}
        onChanged={afterManualChange}
      />

      <SwapTeamsDialog
        open={!!swapDivision}
        onOpenChange={(v) => { if (!v) setSwapDivision(null); }}
        eventId={eventId!}
        division={swapDivision?.division ?? ''}
        teams={swapDivision?.teams ?? []}
        onChanged={afterManualChange}
      />
    </div>
    </SchedulingPageShell>
  );
}
